import { inspectCaiOutputSafety } from "./caiCustomerOutputSafety.js";

const UNSAFE_OUTPUT = /raw\s*(?:prompt|provider\s*response|payload)|service[- ]?role(?:\s*(?:key|token|secret|credential))?|\bbearer\s+[A-Za-z0-9._~+/=-]{16,}\b|\b(?:access|refresh)[-_ ]?token\s*(?:is|=|:)\s*\S+|request\s*id|stack\s*trace|tool\s*(input|use)|blockedReasons|fieldPath|retry|output\s*format|system\s*instruction|prompt\s*instruction|corrected\s+json|json\s+only|provider_response_invalid|quality_gate_failed|internal\s*(diagnostic|process)|deterministic|harness|operator[- ]supplied/i;

const MAX_TEXT = 420;
const MAX_LIST_ITEMS = 8;
const MAX_PROOF_POINTS = 5;
export const CAI_OUTPUT_SCHEMA_VERSION = "cai-output-v7";
// Entity output has its own contract because Brand Guidance is enrichment,
// not a prerequisite when the approved Market Profile and account/run context
// provide a safe entity foundation.
export const ENTITY_OUTPUT_CONTRACT_VERSION = "entity-output-v3";

const CUSTOMER_LABELS = Object.freeze({
  alternatives_replacements: "alternative and replacement comparison signals",
  alternative_replacements: "alternative and replacement comparison signals",
  category_visibility: "category visibility signals",
  entity_clarity: "entity clarity signals",
  competitor_presence: "competitor visibility signals",
  prompt_results: "observed prompt visibility signals",
});

function customerLabel(value) {
  let result = String(value ?? "");
  Object.entries(CUSTOMER_LABELS).forEach(([key, label]) => {
    result = result.replace(new RegExp(`\\b${key}\\b`, "gi"), label);
  });
  return result;
}

function text(value, fallback = "") {
  const normalized = customerLabel(value).replace(/\s+/g, " ").trim();
  return normalized ? normalized.slice(0, MAX_TEXT) : fallback;
}

function list(value) {
  const values = Array.isArray(value) ? value : typeof value === "string" ? [value] : [];
  return values
    .map((item) => {
      if (item && typeof item === "object") {
        const label = text(item.label || item.name || item.key || item.evidence || item.description);
        const itemValue = text(item.value || item.detail || item.summary);
        if (!label) return "";
        if (!itemValue || /^[-+]?\d+(?:\.\d+)?%?$/.test(itemValue)) return label;
        return `${label}: ${itemValue}`;
      }
      return text(item);
    })
    .filter((item) => item && !/^[-+]?\d+(?:\.\d+)?%?$/.test(item) && !UNSAFE_OUTPUT.test(item))
    .slice(0, MAX_LIST_ITEMS);
}

function first(...values) {
  return values.map((value) => text(value)).find(Boolean) || "";
}

function cleanCustomerSummary(value) {
  const normalized = text(value);
  if (!normalized) return "";
  const seen = new Set();
  const deduplicated = normalized
    .split(/(?<=[.!?])\s+/)
    .map((sentence) => sentence.replace(/([.!?])\s*[.!?]+/g, "$1").trim())
    .filter((sentence) => {
      const key = sentence.toLowerCase();
      if (!sentence || seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .join(" ");
  if (deduplicated.length <= MAX_TEXT) return deduplicated;
  const sentenceBoundary = deduplicated.slice(0, MAX_TEXT).match(/^.*?[.!?](?:\s|$)/);
  if (sentenceBoundary?.[0]) return sentenceBoundary[0].trim();
  const wordBoundary = deduplicated.slice(0, MAX_TEXT).replace(/\s+\S*$/, "").trim();
  return wordBoundary ? `${wordBoundary.replace(/[.!?]+$/, "")}.` : "";
}

export function buildCaiOpportunityIdempotencyKey(input = {}) {
  const clean = (value, fallback) => encodeURIComponent(text(value, fallback) || fallback);
  const recommendationIdentity = input.productArea === "local" && input.recommendationSourceKey
    ? input.recommendationSourceKey
    : input.recommendationId || input.recommendationKey;
  const entityContractVersion = isEntityFocusedOpportunity(input)
    ? input.entityOutputContractVersion || ENTITY_OUTPUT_CONTRACT_VERSION
    : null;
  return [
    "cai-opportunity",
    clean(input.accountId, "unknown-account"),
    clean(input.runId, "no-run"),
    clean(recommendationIdentity, "no-recommendation"),
    clean(input.outputType, "cai-output"),
    "schema",
    clean(input.outputSchemaVersion, CAI_OUTPUT_SCHEMA_VERSION),
    ...(input.assetFocused ? ["asset-focused"] : []),
    ...(entityContractVersion ? ["entity-contract", clean(entityContractVersion, ENTITY_OUTPUT_CONTRACT_VERSION)] : []),
    clean(input.actionBriefId, "no-action-brief"),
  ].join(":");
}

function sectionOutline(input) {
  if (Array.isArray(input.sectionOutline) && input.sectionOutline.length) return list(input.sectionOutline);
  const area = input.productArea === "local" ? "local" : "competitive";
  return area === "local"
    ? ["Service and location clarity", "Evidence customers need before choosing", "Recommended local proof", "Next validation step"]
    : ["Decision context", "Comparison and category signal", "Proof points to strengthen", "Next validation step"];
}

function safeCurrentAssetContext(input, fallback = {}) {
  const assetContext = input.assetContext && typeof input.assetContext === "object" && !Array.isArray(input.assetContext)
    ? input.assetContext
    : {};
  const targetLocation = fallback.targetLocation && typeof fallback.targetLocation === "object" ? fallback.targetLocation : {};
  const locationLabel = first(targetLocation.url, targetLocation.path, targetLocation.pageTitle);
  const hasExplicitTargetLocation = Boolean(fallback.targetLocation && typeof fallback.targetLocation === "object");
  const inventoryUnavailable = targetLocation.fallbackReason === "Asset inventory is not available for this account yet.";
  const isCandidateLocation = ["candidate_existing_asset", "operator_configured_candidate"].includes(targetLocation.mode);
  const candidateLabel = first(targetLocation.url, targetLocation.path, targetLocation.pageTitle);
  const candidateSource = targetLocation.evidenceSourcesUsed?.find((value) => ["sitemap_bootstrap", "homepage_link_bootstrap", "operator_configured_target_url"].includes(value));
  const locationSummary = isCandidateLocation && candidateLabel
    ? `${candidateSource === "operator_configured_target_url" ? "Operator-configured target page" : `Candidate page from ${candidateSource === "homepage_link_bootstrap" ? "homepage" : "sitemap"}`}: ${candidateLabel}.`
    : hasExplicitTargetLocation && targetLocation.mode !== "existing_asset"
    ? inventoryUnavailable
      ? "No reviewed account asset inventory is available yet. Create a new competitive proof asset, or run/populate asset inventory before page-specific recommendations."
      : targetLocation.fallbackReason || "No existing reviewed page was found for this opportunity."
    : locationLabel
      ? `Recommended page to update: ${locationLabel}.`
      : "";
  const locationAwareSummary = locationSummary && [
    locationSummary,
    input.currentAssetContext,
    input.current_asset_context,
  ].filter(Boolean).join(" ");
  return cleanCustomerSummary(first(
    locationAwareSummary,
    input.currentAssetContext,
    input.current_asset_context,
    input.currentAssetSummary,
    input.actionBrief?.content_brief?.opportunity_summary,
    assetContext.summary,
    locationSummary && [
      locationSummary,
      fallback.targetAsset && `Focus on ${fallback.targetAsset}`,
      fallback.competitor && `Addressing ${fallback.competitor}`,
      fallback.evidencePattern,
      fallback.expectedResult,
    ].filter(Boolean).join(" "),
    fallback.targetAsset && fallback.evidencePattern && fallback.expectedResult && [
      `Target asset: ${fallback.targetAsset}`,
      fallback.competitor && `Addressing ${fallback.competitor}`,
      fallback.evidencePattern,
      fallback.expectedResult,
    ].filter(Boolean).join(". "),
    assetContext.targetAsset && [
      `Target asset: ${assetContext.targetAsset}`,
      assetContext.competitor && `Addressing ${assetContext.competitor}`,
      assetContext.evidencePattern,
      assetContext.expectedResult,
    ].filter(Boolean).join(". "),
  ));
}

function safeEvidence(input) {
  return first(input.evidencePattern, input.evidence_pattern, input.evidence, input.assetContext?.evidencePattern, input.actionBrief?.why_this_matters);
}

function safeTargetAudience(input) {
  return first(input.targetAudience, input.target_audience, input.assetContext?.audience, input.actionBrief?.content_brief?.audience);
}

function comparisonTarget(value) {
  const match = String(value || "").match(/\b(?:vs\.?|against)\s+(.+)$/i);
  return match ? text(match[1].replace(/[.!?]+$/, "")) : "";
}

function normalizeCompetitiveTargetAsset(value, competitor) {
  const normalized = first(value);
  if (normalized && !/local\s+(?:service|market)|service[- ]area|service area|local visibility/i.test(normalized)) return normalized;
  return competitor
    ? `Competitive comparison and proof asset for ${competitor}`
    : "Competitive proof page or section";
}

function buyerEmpathyContext({ productArea, accountName, competitor, targetAudience, localIntent, evidencePattern, targetAsset, expectedResult }) {
  if (productArea === "competitive") {
    if (!targetAudience || !evidencePattern || !targetAsset || !expectedResult) return "";
    if (!competitor) {
      return cleanCustomerSummary(
        `Visitors and AI systems evaluating ${accountName} need clear, trustworthy information about the selected page and the evidence behind this optimization. The reviewed signals point to ${targetAsset} as the place to make the next decision easier without adding unsupported claims.`,
      );
    }
    return cleanCustomerSummary(
      `Buyers comparing ${accountName} with ${competitor} are likely looking for confidence in fit, proof, and whether the options belong in the same consideration set. The reviewed evidence points to ${targetAsset} as the place to make that comparison easier, reduce hesitation, and support a clearer decision without asking buyers to piece the evidence together.`,
    );
  }
  if (!localIntent && !targetAudience) return "";
  if (!evidencePattern || !targetAsset || !expectedResult) return "";
  return cleanCustomerSummary(
    `Local buyers looking for ${localIntent || targetAudience} may need a clear, trustworthy answer about fit, proximity, availability, and confidence that the business can address the need. The reviewed evidence points to ${targetAsset} as the place to make the next decision easier and more direct.`,
  );
}

function proofPoints(input, evidencePattern) {
  const evidenceItems = Array.isArray(input.evidenceItems) ? input.evidenceItems : [];
  const hasTypedEvidence = evidenceItems.length > 0;
  const reviewedEvidence = hasTypedEvidence
    ? evidenceItems
      .filter((item) => item?.reviewStatus === "reviewed" || ["reviewed_observation", "reviewed_proof"].includes(item?.sourceType || item?.source_type))
      .map((item) => item.text)
    : [
      ...(Array.isArray(input.evidencePoints) ? input.evidencePoints : []),
      ...(Array.isArray(input.proofPoints) ? input.proofPoints : []),
      ...(Array.isArray(input.availableAssetEvidence) ? input.availableAssetEvidence : []),
    ];
  const seen = new Set();
  return list(reviewedEvidence)
    .map((value) => value.replace(/\s*[:=-]\s*[-+]?\d+(?:\.\d+)?%?\s*$/g, "").trim())
    .filter((value) => {
      if (!value || /^[-+]?\d+(?:\.\d+)?%?$/.test(value) || UNSAFE_OUTPUT.test(value) || /candidate page from sitemap|sitemap candidate|not a reviewed asset/i.test(value)) return false;
      const key = value.toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .slice(0, MAX_PROOF_POINTS);
}

function outputTextForSemanticChecks(output) {
  return [
    output?.outputTitle,
    output?.opportunityTitle,
    output?.targetAsset,
    output?.targetAudience,
    output?.assetSummary,
    output?.evidenceSummary,
    output?.recommendedAngle,
    output?.expectedResult,
    output?.nextStep,
    output?.implementationSummary,
    output?.recommendedActions,
    output?.recommendedPageUpdates,
    output?.headingRecommendations,
    output?.pageOrSchemaNotes,
    output?.claimsToSupport,
    output?.FAQsToAnswer,
    output?.proofPoints,
    output?.proofPointsToConfirm,
  ].flatMap((value) => Array.isArray(value) ? value : [value])
    .filter((value) => typeof value === "string")
    .join(" ");
}

function normalizedSemanticWords(value) {
  return String(value || "")
    .toLowerCase()
    .replace(/https?:\/\/[^\s)]+/g, " ")
    .replace(/[^a-z0-9]+/g, " ")
    .split(/\s+/)
    .filter((word) => word.length >= 5 && !new Set([
      "about", "after", "before", "clear", "context", "create", "current", "customer", "evidence", "focus", "focused", "improve", "including", "known", "opportunity", "page", "proof", "review", "reviewed", "strong", "support", "supported", "update", "updated", "using", "where", "with",
    ]).has(word));
}

function assetObjectiveTerms(input = {}) {
  return Array.from(new Set(normalizedSemanticWords([
    input.recommendationTitle,
    input.recommendedAction,
    input.recommended_action,
    input.objective,
    input.evidencePattern,
  ].filter(Boolean).join(" ")))).slice(0, 24);
}

function hasAssetObjectiveAlignment(output, input = {}) {
  const terms = assetObjectiveTerms(input);
  if (!terms.length) return true;
  const outputWords = new Set(normalizedSemanticWords(outputTextForSemanticChecks(output)));
  const matches = terms.filter((term) => outputWords.has(term));
  return matches.length >= (terms.length >= 3 ? 2 : 1);
}

function hasSelfComparison(output, input = {}) {
  const accountName = first(input.accountName, input.businessName).toLowerCase();
  if (!accountName) return false;
  const accountPattern = escapeRegExp(accountName).replace(/\\ /g, "\\s+");
  const serialized = outputTextForSemanticChecks(output);
  if (new RegExp(`${accountPattern}[^.!?]{0,120}\\b(?:vs\\.?|versus|against|compared\\s+(?:with|to)|comparison\\s+(?:with|to))\\b[^.!?]{0,120}${accountPattern}`, "i").test(serialized)) return true;
  return (Array.isArray(output.competitorContext) ? output.competitorContext : [])
    .some((value) => String(value || "").trim().toLowerCase() === accountName);
}

function hasUnreviewedSitemapProof(output, input = {}) {
  const candidates = Array.isArray(input.assetCandidates) ? input.assetCandidates : [];
  const unreviewedLocations = candidates
    .filter((candidate) => candidate?.review_status !== "reviewed" || candidate?.evidence_role === "sitemap_candidate")
    .flatMap((candidate) => [candidate.asset_url, candidate.asset_path, candidate.asset_label])
    .filter(Boolean)
    .map((value) => String(value).toLowerCase());
  const proofText = [output?.proofPoints, output?.claimsToSupport].flatMap((value) => Array.isArray(value) ? value : []).join(" ").toLowerCase();
  return /candidate page from sitemap|sitemap candidate|homepage link candidate/.test(proofText)
    || unreviewedLocations.some((location) => proofText.includes(location));
}

function normalizedEvidenceMatchText(value) {
  return typeof value === "string" ? value.toLowerCase().replace(/\s+/g, " ").trim() : "";
}

function evidenceItemIsReviewed(item) {
  return item?.reviewStatus === "reviewed"
    || ["reviewed_observation", "reviewed_proof"].includes(item?.sourceType || item?.source_type);
}

function evidenceMatchesProofPoint(proofPoint, item) {
  const proofText = normalizedEvidenceMatchText(proofPoint);
  const evidenceText = normalizedEvidenceMatchText(item?.text);
  if (proofText.length < 12 || evidenceText.length < 12) return false;
  return proofText.includes(evidenceText) || evidenceText.includes(proofText);
}

function unreviewedEvidenceForProofPoint(proofPoint, input = {}) {
  return (Array.isArray(input.evidenceItems) ? input.evidenceItems : [])
    .find((item) => !evidenceItemIsReviewed(item) && evidenceMatchesProofPoint(proofPoint, item));
}

function hasUnreviewedEvidenceAsProof(output, input = {}) {
  return (Array.isArray(output?.proofPoints) ? output.proofPoints : [])
    .some((proofPoint) => Boolean(unreviewedEvidenceForProofPoint(proofPoint, input)));
}

function uniqueProofPointList(value) {
  return Array.from(new Set((Array.isArray(value) ? value : [])
    .filter((item) => typeof item === "string")
    .map((item) => item.trim())
    .filter(Boolean)));
}

// Provider output may place observed context in proofPoints. Move only the
// evidence-backed points that the final validator would reject; do not infer,
// upgrade, or invent proof.
export function normalizeCaiProofProvenance(providerOutput, context = {}) {
  if (!providerOutput || typeof providerOutput !== "object" || Array.isArray(providerOutput)) return providerOutput;
  const proofPoints = uniqueProofPointList(providerOutput.proofPoints);
  const existingProofToConfirm = uniqueProofPointList(providerOutput.proofPointsToConfirm);
  const reviewedProof = proofPoints.filter((proofPoint) => !unreviewedEvidenceForProofPoint(proofPoint, context));
  const demotedProof = proofPoints.filter((proofPoint) => Boolean(unreviewedEvidenceForProofPoint(proofPoint, context)));
  return {
    ...providerOutput,
    proofPoints: reviewedProof,
    proofPointsToConfirm: Array.from(new Set([...existingProofToConfirm, ...demotedProof])),
  };
}

function hasSelectedCompetitiveEntity(output, input = {}) {
  const selected = first(input.competitorOrEntity, input.targetEntity, comparisonTarget(input.recommendationTitle || input.opportunityTitle));
  if (!selected) return true;
  const selectedWords = normalizedSemanticWords(selected);
  const declaredContexts = [output?.competitorContext, output?.comparisonContext]
    .flatMap((value) => Array.isArray(value) ? value : [])
    .filter((value) => typeof value === "string")
    .join(" ");
  const outputCorpus = (declaredContexts || [output?.targetAsset, output?.opportunityTitle]
    .filter((value) => typeof value === "string")
    .join(" ")).toLowerCase();
  return selectedWords.length > 0 && selectedWords.some((word) => outputCorpus.includes(word));
}

function proofPointsToConfirm(input, evidencePattern) {
  const candidates = [
    ...(Array.isArray(input.proofPointsToConfirm) ? input.proofPointsToConfirm : []),
    ...(Array.isArray(input.proof_points_to_confirm) ? input.proof_points_to_confirm : []),
    ...(Array.isArray(input.proofPointsNeeded) ? input.proofPointsNeeded : []),
    ...(Array.isArray(input.evidenceItems) ? input.evidenceItems.filter((item) => item?.sourceType === "proof_to_confirm").map((item) => item.text) : []),
  ];
  const values = list(candidates.length ? candidates : [evidencePattern]);
  return Array.from(new Set(values.map((value) => customerLabel(value).trim()).filter(Boolean))).slice(0, MAX_PROOF_POINTS);
}

function hasMeaningfulImplementationDirection(output) {
  if (!output || typeof output !== "object") return false;
  if (typeof output.implementationSummary === "string" && output.implementationSummary.trim()) return true;
  const structuredDirection = [
    ...(Array.isArray(output.recommendedActions) ? output.recommendedActions : []),
    ...(Array.isArray(output.recommendedPageUpdates) ? output.recommendedPageUpdates : []),
    ...(Array.isArray(output.pageOrSchemaNotes) ? output.pageOrSchemaNotes : []),
    ...(Array.isArray(output.headingRecommendations) ? output.headingRecommendations : []),
  ].filter((value) => typeof value === "string" && value.trim());
  return structuredDirection.length > 0;
}

function comparisonClaims({ accountName, competitor, buyerIntent }) {
  if (!competitor) return [];
  const claims = [
    `How ${accountName} should be considered alongside ${competitor}`,
    `Which verified differences help buyers compare ${accountName} with ${competitor}`,
    `What evidence supports the comparison for ${buyerIntent || "this decision"}`,
  ];
  return claims.map(text);
}

function comparisonFaqs({ accountName, competitor, buyerIntent }) {
  if (!competitor || !buyerIntent) return [];
  return [
    `How does ${accountName} compare with ${competitor} for ${buyerIntent}?`,
    `What evidence should buyers review when comparing these options?`,
    `Why should buyers consider ${accountName} alongside ${competitor}?`,
  ].map(text);
}

function targetLocationForOutput(value) {
  const source = value && typeof value === "object" && !Array.isArray(value) ? value : {};
  const mode = ["existing_asset", "target_asset", "gap_backed_new_asset", "candidate_existing_asset", "operator_configured_candidate"].includes(source.mode) ? source.mode : "gap_backed_new_asset";
  const url = first(source.url) || null;
  const path = first(source.path) || null;
  const pageTitle = first(source.pageTitle, source.page_title) || null;
  return {
    mode,
    url,
    path,
    pageTitle,
    assetType: first(source.assetType, source.asset_type) || null,
    matchReason: first(source.matchReason) || null,
    evidenceSourcesUsed: list(source.evidenceSourcesUsed || source.evidenceSources),
    confidence: ["high", "medium", "low"].includes(source.confidence) ? source.confidence : "low",
    fallbackReason: first(source.fallbackReason) || null,
  };
}

function targetLocationLabel(location) {
  if (!["existing_asset", "candidate_existing_asset", "operator_configured_candidate"].includes(location?.mode)) return "";
  return first(location?.url, location?.path, location?.pageTitle);
}

const RECOMMENDED_ASSET_TYPES = new Set(["existing_page", "new_page", "comparison_page", "proof_page", "schema_update", "faq_update"]);
const CREATE_ASSET_TYPES = new Set(["new_page", "comparison_page", "proof_page"]);

export function safeAbsoluteHttpUrl(value) {
  if (typeof value !== "string") return "";
  try {
    const url = new URL(value);
    if (!["http:", "https:"].includes(url.protocol) || url.username || url.password) return "";
    url.search = "";
    url.hash = "";
    return url.toString();
  } catch {
    return "";
  }
}

function assetActionForType(type) {
  if (CREATE_ASSET_TYPES.has(type)) return "create_new_asset";
  if (["existing_page", "schema_update", "faq_update"].includes(type)) return "improve_existing_asset";
  return "";
}

export function isEntityFocusedOpportunity(value = {}) {
  const source = value && typeof value === "object" ? value : {};
  if (source.entityFocused === true) return true;
  const signals = [
    source.entityArea,
    source.entity_area,
    source.recommendationType,
    source.recommendation_type,
    source.opportunityType,
    source.opportunity_type,
    source.recommendedOutput,
    source.recommended_output,
    source.recommendationTitle,
    source.recommendation_title,
    source.opportunityTitle,
    source.opportunity_title,
    source.targetAsset,
    source.target_asset,
    source.recommendedAction,
    source.recommended_action,
  ].map((item) => String(item ?? "").toLowerCase().replace(/[^a-z0-9]+/g, " ").trim()).filter(Boolean);
  return signals.some((signal) => (
    signal === "identity clarity"
    || signal.includes("entity clarity")
    || signal.includes("entity foundation")
    || signal.includes("audit primary entity")
    || signal.includes("primary entity foundation")
    || signal.includes("entity audit")
    || signal.includes("audit entity")
  ));
}

function looksLikeInternalEntityLabel(value) {
  const normalized = String(value ?? "").trim().toLowerCase();
  return !normalized
    || /^[-+]?\d+(?:\.\d+)?%?$/.test(normalized)
    || /^(?:weak|absent|developing|moderate|strong|unknown|n\/a)$/.test(normalized)
    || /^(?:local|buyer)\s+intent$/.test(normalized)
    || /^(?:identity|entity)[ _-]?clarity$/.test(normalized)
    || /^[a-z][a-z0-9]*(?:_[a-z0-9]+)+$/.test(normalized);
}

function safeEntityName(...values) {
  return values
    .map((value) => text(value))
    .find((value) => value && !looksLikeInternalEntityLabel(value)) || "the business";
}

function safeEntityEvidence(value, entityName) {
  const normalized = text(value);
  const fallback = `The reviewed entity signals indicate that ${entityName} needs a more consistent foundation across its account and homepage context.`;
  if (!normalized || looksLikeInternalEntityLabel(normalized)) return fallback;
  if (/(?:identity[_ -]clarity|entity[_ -]clarity|alternatives?[_ -]replacements?|local[_ -]comparison)/i.test(normalized)) return fallback;
  return normalized
    .replace(/\b(?:weak|absent)\b/gi, "not yet confirmed")
    .replace(/\b(?:developing|moderate)\b/gi, "still developing")
    .replace(/\bidentity\s+clarity\b/gi, "entity clarity")
    .replace(/\bentity\s+clarity\b/gi, "entity foundation")
    .replace(/\s+/g, " ")
    .trim();
}

function specificEntityEvidence(value) {
  const normalized = text(value);
  if (!normalized || looksLikeInternalEntityLabel(normalized)) return "";
  if (/(?:identity[_ -]clarity|entity[_ -]clarity|alternatives?[_ -]replacements?|local[_ -]comparison|\b(?:weak|absent)\b)/i.test(normalized)) return "";
  return normalized
    .replace(/\b(?:developing|moderate)\b/gi, "still developing")
    .replace(/\s+/g, " ")
    .trim();
}

function operatorTargetSupportingUrl(context = {}) {
  const targetLocation = targetLocationForOutput(context.targetLocation);
  if (targetLocation.mode !== "operator_configured_candidate"
    || !targetLocation.evidenceSourcesUsed.includes("operator_configured_target_url")) return "";
  return safeAbsoluteHttpUrl(targetLocation.url);
}

function hasBrandGuidance(value) {
  const guidance = value && typeof value === "object" ? value : {};
  return Boolean(
    guidance.brandVoiceSummary
    || guidance.positioningNotes
    || guidance.audienceNotes
    || (Array.isArray(guidance.preferredTerms) && guidance.preferredTerms.length)
    || (Array.isArray(guidance.approvedClaims) && guidance.approvedClaims.length),
  );
}

function hasMarketProfile(value) {
  const profile = value && typeof value === "object" ? value : {};
  return Boolean(
    String(profile.status || "").trim().toLowerCase() === "approved"
    ||
    profile.primary_category
    || profile.category_description
    || profile.plain_language_description
    || profile.buyer_types?.length
    || profile.jobs_to_be_done?.length
    || profile.direct_competitor_definition
    || profile.adjacent_competitor_definition
    || profile.platform_partner_definition
    || profile.exclusion_definition
    || profile.known_direct_competitors?.length
    || profile.known_adjacent_competitors?.length
    || profile.known_platforms_partners?.length
    || profile.known_exclusions?.length,
  );
}

function entityContextSources(input = {}, entityContext = {}, brandGuidance = {}) {
  const sources = [];
  if (first(entityContext.accountName, entityContext.businessName, input.accountName)) sources.push("account_profile");
  if (first(entityContext.websiteUrl, entityContext.websiteDomain, input.websiteUrl, input.websiteDomain)) sources.push("homepage");
  if (hasBrandGuidance(brandGuidance)) sources.push("brand_guidance");
  if (hasMarketProfile(entityContext.marketProfile)) sources.push("market_profile");
  if (first(input.evidencePattern, input.evidence, entityContext.entityEvidence)
    || (Array.isArray(input.reviewedSignals) && input.reviewedSignals.length)) sources.push("run_evidence");
  const evidenceSources = Array.isArray(input.targetLocation?.evidenceSourcesUsed) ? input.targetLocation.evidenceSourcesUsed : [];
  if (evidenceSources.some((source) => source === "sitemap_bootstrap" || source === "homepage_link_bootstrap" || source === "sitemap_candidate")) {
    sources.push("sitemap_candidates");
  }
  return Array.from(new Set(sources));
}

function buildEntityCaiOpportunityOutput(input = {}) {
  const entityContext = input.entityContext && typeof input.entityContext === "object" ? input.entityContext : {};
  const accountName = first(input.accountName, entityContext.accountName, entityContext.businessName);
  const entityName = safeEntityName(
    entityContext.accountName,
    entityContext.businessName,
    input.accountName,
    input.businessName,
    entityContext.entityName,
    input.competitorOrEntity,
    input.targetEntity,
    accountName,
  );
  const brandGuidance = entityContext.brandGuidance && typeof entityContext.brandGuidance === "object"
    ? entityContext.brandGuidance
    : input.accountContext?.brandGuidance && typeof input.accountContext.brandGuidance === "object"
      ? input.accountContext.brandGuidance
      : {};
  const rawEntityEvidence = first(input.evidencePattern, entityContext.entityEvidence, input.reviewedSignals?.[0]?.value);
  const specificEvidence = specificEntityEvidence(rawEntityEvidence);
  const evidencePattern = safeEntityEvidence(rawEntityEvidence, entityName);
  const expectedResultCandidate = first(input.expectedResult);
  const expectedResult = !expectedResultCandidate || looksLikeInternalEntityLabel(expectedResultCandidate) || /\blocal\s+intent\b/i.test(expectedResultCandidate)
    ? `A more consistent entity foundation for ${entityName} across account and homepage context.`
    : expectedResultCandidate;
  const targetAudience = `AI systems and buyers evaluating ${entityName}`;
  const marketProfile = entityContext.marketProfile && typeof entityContext.marketProfile === "object" ? entityContext.marketProfile : {};
  const accountCategory = first(
    entityContext.accountCategory,
    entityContext.category,
    marketProfile.primary_category,
    marketProfile.category_description,
  );
  const market = first(
    entityContext.market,
    entityContext.marketScope,
    marketProfile.buyer_types,
    marketProfile.jobs_to_be_done,
    marketProfile.direct_competitor_definition,
  );
  const productsServices = first(
    entityContext.productsServices,
    entityContext.products,
    entityContext.services,
    input.productsServices,
    input.products,
    input.services,
    marketProfile.plain_language_description,
    marketProfile.jobs_to_be_done,
  );
  const targetLocation = targetLocationForOutput(input.targetLocation);
  const currentAssetContext = first(
    entityContext.websiteUrl && `Account/entity context for ${entityName || accountName} uses ${entityContext.websiteUrl}.`,
    entityContext.websiteDomain && `Account/entity context for ${entityName || accountName} uses ${entityContext.websiteDomain}.`,
    `Reviewed account and homepage context for ${entityName}.`,
  );
  const entityInput = {
    ...input,
    productArea: "local",
    accountName,
    opportunityTitle: `Entity foundation update for ${entityName}`,
    recommendationTitle: `Entity foundation update for ${entityName}`,
    targetAsset: "Entity foundation update",
    recommended_asset_type: "schema_update",
    recommended_asset_label: "Entity foundation update",
    targetAudience,
    evidencePattern,
    expectedResult,
    currentAssetContext,
    recommendedAction: "Update the entity foundation fields after review.",
    nextStep: "Re-run entity clarity checks after the reviewed foundation updates.",
    objective: first(input.objective, "Confirm the primary entity's reviewed name, domain, category, and approved proof signals."),
    targetLocation,
  };
  const built = buildStandardCaiOpportunityOutput(entityInput);
  if (built.status !== "ready" || !built.output) return built;
  const aliases = Array.isArray(entityContext.knownAliases) ? entityContext.knownAliases : [];
  const guidancePresent = Boolean(
    brandGuidance.brandVoiceSummary
    || brandGuidance.positioningNotes
    || brandGuidance.audienceNotes
    || (Array.isArray(brandGuidance.preferredTerms) && brandGuidance.preferredTerms.length)
    || (Array.isArray(brandGuidance.approvedClaims) && brandGuidance.approvedClaims.length),
  );
  const entityFindings = [
    specificEvidence ? `Reviewed opportunity evidence: ${specificEvidence}` : "",
    guidancePresent ? "Reviewed Brand Guidance is available as the source of truth for preferred wording and approved claims." : "Brand Guidance is not currently available. Add it later to improve specificity and approved-claims handling.",
    entityName ? `The reviewed account identifies the primary entity as ${entityName}.` : "",
    first(entityContext.websiteUrl, entityContext.websiteDomain) ? `The account is associated with ${first(entityContext.websiteUrl, entityContext.websiteDomain)}.` : "",
    accountCategory ? `The account category is recorded as ${accountCategory}.` : "",
    market ? `The account market or audience context is recorded as ${market}.` : "",
  ].filter(Boolean).slice(0, 4);
  const fieldsToConfirm = [
    !accountName ? "Primary brand name" : "",
    !first(entityContext.websiteUrl, entityContext.websiteDomain) ? "Website domain" : "",
    !accountCategory ? "Business category" : "",
    !productsServices ? "Products/services" : "",
    !market ? "Market or audience language" : "",
    !specificEvidence ? "Reviewed proof points" : "",
    !guidancePresent ? "Brand Guidance in Admin Onboarding" : "",
  ].filter(Boolean);
  if (!fieldsToConfirm.length) fieldsToConfirm.push("Primary brand wording and reviewed entity references");
  if (!specificEvidence && !guidancePresent) {
    entityFindings.push(`The available reviewed context does not yet confirm ${fieldsToConfirm.slice(0, 3).join(", ")}.`);
  }
  const missingEntityContextRecommendation = guidancePresent
    ? (fieldsToConfirm.some((field) => /needs confirmation|Products\/services|Market|category/i.test(field))
      ? `Confirm ${fieldsToConfirm.filter((field) => /needs confirmation|Products\/services|Market|category/i.test(field)).slice(0, 3).join(", ")} before applying entity changes.`
      : "")
    : "Add Brand Guidance in Admin Onboarding before applying entity changes.";
  const recommendedEntityUpdate = guidancePresent
    ? `Align the homepage and organization/entity references to the reviewed Brand Guidance for ${entityName}, then confirm the category and market wording used across the account.`
    : `The highest-value next step is to add Brand Guidance in Admin Onboarding to confirm ${entityName}'s primary business name, preferred wording, category, products/services, market, and approved proof points.`;
  const whereToApply = [
    "Brand Guidance in Admin Onboarding",
    "Homepage",
    "Organization/schema/entity markup",
  ];
  const recommendedActions = guidancePresent
    ? [
      recommendedEntityUpdate,
      !accountCategory ? "Confirm the business category before aligning visible copy or entity markup." : "Keep the recorded business category consistent wherever the entity is described.",
      !productsServices ? "Confirm the products/services language before adding it to visible copy or structured data." : "Keep reviewed products/services language consistent across the known account context.",
      !market ? "Confirm the market or audience language before applying it to the homepage or entity fields." : "Keep the recorded market or audience language consistent across entity references.",
      !specificEvidence ? "Capture reviewed proof points before adding new claims, aliases, services, or certifications." : "Use the reviewed opportunity evidence to decide which proof language can be carried forward.",
    ].filter(Boolean).slice(0, 5)
    : [
      recommendedEntityUpdate,
      "After Brand Guidance is approved, align the homepage and organization/entity references to that wording.",
      "Re-run entity clarity checks after the reviewed fields are confirmed.",
    ];
  const output = {
    ...built.output,
    targetAsset: "Entity foundation update",
    recommended_asset_type: "schema_update",
    recommended_asset_url: null,
    recommended_asset_label: "Entity foundation update",
    asset_action: "improve_existing_asset",
    targetLocation,
    assetSummary: `Entity foundation context for ${entityName || "this business"}.`,
    evidenceSummary: entityFindings.join(" "),
    evidencePattern: entityFindings.join(" "),
    currentAssetContext: currentAssetContext || built.output.currentAssetContext,
    expectedResult,
    targetAudience,
    entityContext: {
      accountName: accountName || null,
      businessName: entityContext.businessName || null,
      websiteUrl: entityContext.websiteUrl || null,
      websiteDomain: entityContext.websiteDomain || null,
      accountCategory: accountCategory || null,
      accountType: entityContext.accountType || null,
      market: market || null,
      productsServices: productsServices || null,
      knownAliases: aliases,
      brandGuidance: guidancePresent ? brandGuidance : null,
      brandGuidanceStatus: guidancePresent ? "account_provided" : "not_configured",
    },
    missing_enrichment_inputs: guidancePresent ? [] : ["Brand Guidance"],
    missingEnrichmentInputs: guidancePresent ? [] : ["Brand Guidance"],
    brand_guidance_required: false,
    brandGuidanceRequired: false,
    entityFieldsToConfirm: fieldsToConfirm,
    homepageProofAreas: [
      "Confirm the homepage communicates the same primary entity name and category.",
      "Review visible proof areas before adding or changing entity/schema markup.",
    ],
    recommendedActions,
    claimsToSupport: Array.isArray(brandGuidance.approvedClaims) ? list(brandGuidance.approvedClaims) : [],
    claimsToAvoid: Array.isArray(brandGuidance.claimsToAvoid) ? list(brandGuidance.claimsToAvoid) : [],
    FAQsToAnswer: [],
    sectionOutline: [
      "Confirm the primary entity",
      "Align brand and category language",
      "Strengthen homepage and entity proof",
      "Review schema/entity markup",
      "Re-run entity clarity checks",
    ],
    recommendedStructure: [
      "Confirm the primary entity",
      "Align brand and category language",
      "Strengthen homepage and entity proof",
      "Review schema/entity markup",
      "Re-run entity clarity checks",
    ],
    pageOrSchemaNotes: [
      "Review entity and organization schema only against confirmed account details.",
      "Keep homepage language, domain references, and approved Brand Guidance consistent.",
    ],
    implementationSummary: guidancePresent
      ? `Use the account-provided Brand Guidance as the source of truth. Align ${entityName}'s homepage copy, category language, and organization/entity references to that reviewed guidance. Do not add aliases, claims, services, certifications, or proof points unless they are reviewed and supported.`
      : `The account name and domain are available, but Brand Guidance is not currently available. Add it later to improve specificity and approved-claims handling, then align ${entityName}'s homepage copy and organization/entity references without adding unsupported claims.`,
    recommendedPageUpdates: [],
    headingRecommendations: [],
    faqSchemaRecommendations: [],
    validationNextStep: "Update or confirm the entity foundation fields, then re-run entity clarity checks to verify that AI responses describe the business more consistently.",
    nextStep: "Update or confirm the entity foundation fields, then re-run entity clarity checks.",
    recommendedAngle: recommendedEntityUpdate,
    buyerEmpathyContext: `Buyers and AI systems need confidence that ${entityName} is the same entity across its name, domain, category, and supporting proof. The review should reduce ambiguity without adding unsupported claims.`,
    entityFocus: "Entity focus",
    entityFindings,
    entity_findings: entityFindings,
    recommendedEntityUpdate,
    recommended_entity_update: recommendedEntityUpdate,
    whereToApply,
    where_to_apply: whereToApply,
    missingContextRecommendation: missingEntityContextRecommendation,
    missing_context_recommendation: missingEntityContextRecommendation,
    localIntent: null,
    local_intent: null,
    buyerIntent: null,
    buyer_intent: null,
    competitorContext: [],
  };
  const entityFieldsToReview = [
    accountCategory ? "Business category" : "Business category needs confirmation",
    productsServices ? "Products/services represented in reviewed account materials" : "Products/services need confirmation",
    market ? "Market or audience language" : "Market or audience language needs confirmation",
  ].slice(0, 3);
  const brandNameConsistencyChecks = [
    "Primary entity name matches the account and homepage language.",
    "Canonical domain and entity references are consistent.",
  ];
  const schemaEntityRecommendations = [
    "Review organization/entity markup only against confirmed account details.",
    "Confirm sameAs and entity references only when they are reviewed and supported.",
  ];
  const reviewedEntityInputs = [
    ...entityFindings.slice(0, 3),
  ];
  const contextSources = entityContextSources(entityInput, entityContext, brandGuidance);
  Object.assign(output, {
    entity_action: "update_entity_foundation",
    entityAction: "update_entity_foundation",
    primary_entity_name: entityName || accountName || null,
    primaryEntityName: entityName || accountName || null,
    primary_entity_domain: entityContext.websiteDomain || entityContext.websiteUrl || null,
    primaryEntityDomain: entityContext.websiteDomain || entityContext.websiteUrl || null,
    primary_entity_homepage: entityContext.websiteUrl || entityContext.websiteDomain || null,
    primaryEntityHomepage: entityContext.websiteUrl || entityContext.websiteDomain || null,
    entity_fields_to_confirm: output.entityFieldsToConfirm,
    entityFieldsToConfirm: output.entityFieldsToConfirm,
    entity_inputs_to_confirm: output.entityFieldsToConfirm,
    entityInputsToConfirm: output.entityFieldsToConfirm,
    entity_fields_to_review: entityFieldsToReview,
    entityFieldsToReview: entityFieldsToReview,
    brand_name_consistency_checks: brandNameConsistencyChecks,
    brandNameConsistencyChecks,
    schema_entity_recommendations: schemaEntityRecommendations,
    schemaEntityRecommendations,
    reviewed_entity_inputs: reviewedEntityInputs,
    reviewedEntityInputs: reviewedEntityInputs,
    entity_context_sources: contextSources,
    entityContextSources: contextSources,
    entity_findings: entityFindings,
    entityFindings,
    recommendedEntityUpdate,
    recommended_entity_update: recommendedEntityUpdate,
    whereToApply,
    where_to_apply: whereToApply,
    missingContextRecommendation: missingEntityContextRecommendation,
    missing_context_recommendation: missingEntityContextRecommendation,
    proofPoints: [],
    proofPointsNeeded: [],
    assetGap: "",
  });
  const quality = validateEntityCaiOpportunityOutput(output, { ...entityInput, entityFocused: true });
  return quality.status === "ready"
    ? { status: "ready", reason: "quality_gate_passed", reasonCode: null, qualityFailures: [], output }
    : quality;
}

export function buildCaiOpportunityOutput(input = {}) {
  return isEntityFocusedOpportunity(input) ? buildEntityCaiOpportunityOutput(input) : buildStandardCaiOpportunityOutput(input);
}

export function buildCaiDeterministicEntityContextFallback(input = {}) {
  const entityContext = input.entityContext && typeof input.entityContext === "object" ? input.entityContext : {};
  const accountName = first(input.accountName, entityContext.accountName, entityContext.businessName);
  const entityName = safeEntityName(entityContext.accountName, entityContext.businessName, input.accountName, entityContext.entityName, input.competitorOrEntity, input.targetEntity, accountName);
  const domainOrHomepage = first(entityContext.websiteDomain, entityContext.websiteUrl, input.websiteDomain, input.websiteUrl);
  if ((!accountName && !entityName) || !domainOrHomepage) {
    const qualityFailures = ["entity_context_insufficient"];
    return {
      status: "needs_review",
      reason: "entity_context_insufficient",
      reasonCode: "entity_context_insufficient",
      qualityFailures,
      qualityFailureDetails: describeCaiOutputQualityFailures(qualityFailures),
      output: null,
    };
  }
  const built = buildEntityCaiOpportunityOutput({ ...input, entityFocused: true });
  if (built.status !== "ready" || !built.output) {
    const qualityFailures = Array.from(new Set(built.qualityFailures || ["entity_context_insufficient"]));
    return {
      ...built,
      status: "needs_review",
      reason: built.reason || "entity_context_insufficient",
      reasonCode: built.reasonCode || "entity_context_insufficient",
      qualityFailures,
      qualityFailureDetails: describeCaiOutputQualityFailures(qualityFailures),
      output: null,
    };
  }
  const brandGuidance = entityContext.brandGuidance && typeof entityContext.brandGuidance === "object" ? entityContext.brandGuidance : {};
  const output = {
    ...built.output,
    entity_brief_source: "deterministic_entity_context_fallback",
    entity_context_sources: entityContextSources(input, entityContext, brandGuidance),
    missing_enrichment_inputs: hasBrandGuidance(brandGuidance) ? [] : ["Brand Guidance"],
    missingEnrichmentInputs: hasBrandGuidance(brandGuidance) ? [] : ["Brand Guidance"],
    brand_guidance_required: false,
    brandGuidanceRequired: false,
  };
  const quality = validateEntityCaiOpportunityOutput(output, { ...input, entityFocused: true });
  return quality.status === "ready"
    ? {
      status: "ready",
      reason: "deterministic_entity_context_ready",
      reasonCode: null,
      qualityFailures: [],
      output: quality.output,
      entityBriefSource: "deterministic_entity_context_fallback",
    }
    : quality;
}

/**
 * Fill only an omitted implementation brief from the reviewed structured
 * opportunity. This is deliberately narrower than the provider output
 * normalizer: insufficient context remains a field-level review failure.
 */
export function completeCaiImplementationBrief(providerOutput, context = {}) {
  const output = providerOutput && typeof providerOutput === "object" && !Array.isArray(providerOutput)
    ? { ...providerOutput }
    : {};
  const providerBrief = typeof output.implementationSummary === "string" ? output.implementationSummary.trim() : "";
  if (providerBrief) return { output, implementationBriefSource: "provider" };

  const targetLocation = targetLocationForOutput(context.targetLocation);
  const supportingUrl = operatorTargetSupportingUrl(context);
  const supportingPath = supportingUrl ? new URL(supportingUrl).pathname : "";
  const type = RECOMMENDED_ASSET_TYPES.has(output.recommended_asset_type) ? output.recommended_asset_type : "";
  const expectedAction = assetActionForType(type);
  const declaredAction = typeof output.asset_action === "string" ? output.asset_action.trim() : "";
  const actionPattern = expectedAction === "create_new_asset" ? /\b(create|build|develop|write|produce|draft)\b/i : /\b(improve|update|strengthen|clarify|revise|add|refresh)\b/i;
  const actionIsGrounded = declaredAction === expectedAction
    || [...(Array.isArray(output.recommendedActions) ? output.recommendedActions : []), context.nextStep, context.recommendedAction]
      .some((value) => typeof value === "string" && actionPattern.test(value));
  const opportunityTitle = first(output.opportunityTitle, context.opportunityTitle, context.recommendationTitle);
  const recommendationType = first(context.recommendationType, context.opportunityType);
  const recommendedOutput = first(context.recommendedOutput, output.recommendedOutput);
  const targetEntity = first(context.targetEntity, context.competitorOrEntity, output.competitorContext?.[0]);
  const label = first(output.recommended_asset_label, output.targetAsset);
  const evidence = first(context.evidencePattern, output.evidencePattern, output.evidenceSummary);
  const expectedResult = first(output.expectedResult, context.expectedResult);
  const rationale = first(output.recommendedAngle, context.recommendationSummary, context.whyItMatters, evidence, expectedResult);
  const operatorRole = output.operatorTargetContext?.role || output.supporting_context_role;
  const supportIsSeparated = operatorRole === "supporting_context"
    && output.supporting_context_url === supportingUrl;

  const canBuild = context.productArea === "local"
    && targetLocation.mode === "operator_configured_candidate"
    && Boolean(supportingUrl && supportingPath && opportunityTitle && recommendationType && recommendedOutput && targetEntity && type && label && expectedAction && actionIsGrounded && rationale && evidence && expectedResult && supportIsSeparated)
    && (expectedAction !== "create_new_asset" || !output.recommended_asset_url);
  if (!canBuild) return { output, implementationBriefSource: null };

  const actionVerb = expectedAction === "create_new_asset" ? "Create" : "Improve";
  const briefParts = [
    `${actionVerb} ${label}.`,
    `Keep the operator-configured ${supportingPath} page as supporting site context only, not as the page to edit.`,
    `Ground the work in the reviewed opportunity evidence: ${evidence.slice(0, 150)}.`,
    `Use only proof supported by reviewed materials, and validate against the expected result: ${expectedResult.slice(0, 130)}.`,
  ];
  const implementationSummary = briefParts.join(" ").replace(/\s+/g, " ").trim();
  if (implementationSummary.length > 900 || UNSAFE_OUTPUT.test(implementationSummary)) {
    return { output, implementationBriefSource: null };
  }
  return {
    output: { ...output, implementationSummary },
    implementationBriefSource: "deterministic_context_fallback",
  };
}

function escapeRegExp(value) {
  return String(value).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function recommendedAssetForContext(providerOutput, context, productArea) {
  const output = providerOutput && typeof providerOutput === "object" ? providerOutput : {};
  const targetLocation = targetLocationForOutput(context.targetLocation);
  const operatorTarget = targetLocation.mode === "operator_configured_candidate"
    && targetLocation.evidenceSourcesUsed.includes("operator_configured_target_url");
  const candidateUrl = operatorTarget ? safeAbsoluteHttpUrl(targetLocation.url) : "";
  const explicitType = first(output.recommended_asset_type, output.recommendedAssetType);
  const explicitLabel = first(output.recommended_asset_label, output.recommendedAssetLabel);
  const explicitUrl = safeAbsoluteHttpUrl(first(output.recommended_asset_url, output.recommendedAssetUrl));

  const reviewedExistingUrl = targetLocation.mode === "existing_asset" ? safeAbsoluteHttpUrl(targetLocation.url) : "";
  const explicitExistingUrlIsGrounded = explicitType !== "existing_page"
    || Boolean(explicitUrl && (operatorTarget ? explicitUrl === candidateUrl : explicitUrl === reviewedExistingUrl));
  const explicitNewAssetHasNoUrl = explicitType === "existing_page" || !explicitUrl;
  if (RECOMMENDED_ASSET_TYPES.has(explicitType) && explicitLabel && explicitExistingUrlIsGrounded
    && explicitNewAssetHasNoUrl
    && (!operatorTarget || explicitType !== "existing_page" || explicitUrl === candidateUrl)) {
    return {
      type: explicitType,
      label: explicitLabel,
      url: explicitType === "existing_page" ? explicitUrl || null : null,
      operatorRole: operatorTarget ? (explicitType === "existing_page" ? "candidate_existing_asset" : "supporting_context") : null,
    };
  }

  if (operatorTarget) {
    const title = first(context.recommendationTitle, context.opportunityTitle, output.opportunityTitle);
    const competitor = first(context.competitorOrEntity, context.targetEntity, context.assetContext?.competitor, comparisonTarget(title));
    const accountName = first(context.accountName);
    const prescriptionSignals = [
      title,
      context.recommendationType,
      context.recommendedOutput,
      context.recommendedAction,
      context.expectedResult,
    ].filter(Boolean).join(" ");
    const comparisonIntent = Boolean(competitor && /\b(compare|comparison|against|vs\.?|proof)\b/i.test(prescriptionSignals));
    if (comparisonIntent && accountName) {
      return {
        type: /\bproof\b/i.test(prescriptionSignals) ? "proof_page" : "comparison_page",
        label: `New comparison/proof page for ${accountName} vs. ${competitor}`,
        url: null,
        operatorRole: "supporting_context",
      };
    }
    return { type: "", label: "", url: null, operatorRole: "supporting_context" };
  }

  const existingLocation = targetLocation.mode === "existing_asset" ? safeAbsoluteHttpUrl(targetLocation.url) : "";
  const label = first(output.recommended_asset_label, output.recommendedAssetLabel, output.targetAsset);
  const type = RECOMMENDED_ASSET_TYPES.has(explicitType) && explicitExistingUrlIsGrounded
    ? explicitType
    : existingLocation ? "existing_page" : label ? (productArea === "local" ? "new_page" : "comparison_page") : "";
  return {
    type,
    label: label && label !== existingLocation ? label : existingLocation ? `Existing page to improve: ${targetLocation.pageTitle || targetLocation.path || existingLocation}` : "",
    url: type === "existing_page" ? existingLocation || null : null,
    operatorRole: null,
  };
}

function prescribedActions({ nextStep, targetLocation, targetAsset, competitor, productArea }) {
  if (!nextStep) return [];
  const location = targetLocationLabel(targetLocation);
  const actions = [nextStep];
  if (location) {
    actions.push(productArea === "local"
      ? `Update ${location} with clear service, location, and trust proof.`
      : `Update ${location} with comparison proof supported by the reviewed evidence.`);
    actions.push(productArea === "local"
      ? "Clarify the service and location questions buyers need answered."
      : "Add concise comparison FAQs and matching FAQ schema where appropriate.");
    actions.push(productArea === "local"
      ? "Strengthen headings, body copy, and internal links around the local decision."
      : "Strengthen headings, body copy, and internal links around the comparison decision.");
  } else {
    actions.push(productArea === "local"
      ? "Create a focused local service and location proof asset."
      : `Create a competitive comparison and proof asset${competitor ? ` for ${competitor}` : ""}.`);
    actions.push(`Use ${targetAsset || (productArea === "local" ? "a focused local service page or section" : "a focused comparison page or section")} with verified evidence and clear buyer questions.`);
    actions.push(productArea === "local"
      ? "Add concise service and location FAQs with matching schema where appropriate."
      : "Add concise comparison FAQs and matching FAQ schema where appropriate.");
  }
  return Array.from(new Set(actions.map(text).filter(Boolean))).slice(0, MAX_LIST_ITEMS);
}

function implementationFields({ input, productArea, targetLocation, targetAsset, competitor, targetAudience, evidencePattern, buyerIntent, localIntent, expectedResult }) {
  const location = targetLocationLabel(targetLocation);
  const hasReviewedPage = Boolean(location && ["existing_asset", "candidate_existing_asset"].includes(targetLocation.mode));
  const suppliedUpdates = list(input.recommendedPageUpdates || input.recommended_page_updates);
  const suppliedHeadings = list(input.headingRecommendations || input.heading_recommendations);
  const suppliedFaqSchema = list(input.faqSchemaRecommendations || input.faq_schema_recommendations);
  const subject = competitor ? ` for ${competitor}` : "";
  const inventoryUnavailable = targetLocation.fallbackReason === "Asset inventory is not available for this account yet.";
  const reviewedPageNotFound = targetLocation.fallbackReason === "No existing reviewed page was found after checking account asset inventory.";
  const recommendedPageUpdates = suppliedUpdates.length ? suppliedUpdates : hasReviewedPage
    ? [
      `Update ${location} with ${productArea === "local" ? "service, location, and trust proof" : "comparison proof"} supported by the reviewed evidence.`,
      productArea === "local" ? "Clarify service availability and location coverage for the priority need." : `Clarify the comparison criteria buyers use when evaluating ${competitor || "the options"}.`,
      "Strengthen related headings and internal links without adding unsupported claims.",
    ].map(text)
    : [
      productArea === "local" ? "Create a focused local service and location proof asset." : `Create a focused competitive proof asset${subject}.`,
      `Use ${targetAsset || (productArea === "local" ? "a service and location page" : "a comparison page or section")} to address the reviewed evidence pattern.`,
      "Define the page path only after the asset scope and reviewed proof are confirmed.",
    ].map(text);
  const headingRecommendations = suppliedHeadings.length ? suppliedHeadings : productArea === "local"
    ? ["Service and location clarity", "What local buyers should know", "Proof that supports the next decision"]
    : ["How buyers compare the options", "Evidence that supports the comparison", "What to review before choosing"];
  const faqSchemaRecommendations = suppliedFaqSchema.length ? suppliedFaqSchema : productArea === "local"
    ? ["Add concise FAQs about service availability and location coverage.", "Use FAQ schema only for reviewed answers that match the visible page."]
    : ["Add concise FAQs for comparison and alternative evaluation.", "Use FAQ schema only for reviewed answers that match the visible page."];
  const implementationSummary = inventoryUnavailable
    ? "No reviewed account asset inventory is available yet. Create a new competitive proof asset, or run/populate asset inventory before page-specific recommendations."
    : reviewedPageNotFound
      ? "No existing reviewed page was found after checking account asset inventory. Create a new reviewed asset only after confirming the scope and proof needed."
    : hasReviewedPage
    ? `Recommended page to update: ${location}. Apply the reviewed ${productArea === "local" ? "local visibility" : "comparison"} improvements without adding unsupported claims.`
    : `No existing reviewed page was found. Create a new ${productArea === "local" ? "local service and location" : "competitive comparison and proof"} asset${subject}.`;
  const validationNextStep = first(
    input.validationNextStep,
    input.validation_next_step,
    inventoryUnavailable
      ? "Populate or review account asset inventory, then reassess page-specific recommendations."
      : "",
    hasReviewedPage
      ? `Re-run the reviewed ${productArea === "local" ? "local visibility" : "comparison"} checks and confirm the page addresses the identified evidence gap.`
      : `Review the new ${productArea === "local" ? "local service and location" : "competitive proof"} asset scope, then re-run the relevant visibility checks.`,
  );
  return {
    implementationSummary: text(implementationSummary),
    recommendedPageUpdates,
    headingRecommendations,
    faqSchemaRecommendations,
    validationNextStep: text(validationNextStep),
    targetAudience,
    evidencePattern,
    buyerIntent,
    localIntent,
    expectedResult,
  };
}

function safeAssetContext(input, targetAsset, competitor, evidencePattern, expectedResult, recommendedAngle) {
  const source = input.assetContext && typeof input.assetContext === "object" && !Array.isArray(input.assetContext)
    ? input.assetContext
    : {};
  const allowedModes = ["existing_asset", "candidate_existing_asset", "operator_configured_candidate", "target_asset", "gap_backed_new_asset"];
  const mode = allowedModes.includes(source.mode)
    ? source.mode
    : input.currentAssetContext
      ? "existing_asset"
      : targetAsset
        ? "gap_backed_new_asset"
      : null;
  return {
    mode,
    targetAsset: first(targetAsset, source.targetAsset, source.target_asset) || null,
    competitor: first(source.competitor, competitor) || null,
    objective: first(source.objective, expectedResult, recommendedAngle) || null,
    evidencePattern: first(source.evidencePattern, source.evidence_pattern, evidencePattern) || null,
    expectedResult: first(source.expectedResult, source.expected_result, expectedResult) || null,
  };
}

function buildStandardCaiOpportunityOutput(input = {}) {
  const productArea = input.productArea === "local" ? "local" : "competitive";
  const accountName = text(input.accountName, "This account");
  const opportunityTitle = first(input.recommendationTitle, input.opportunityTitle, input.actionBrief?.title);
  const targetAudience = safeTargetAudience(input);
  const evidencePattern = safeEvidence(input);
  const expectedResult = first(input.expectedResult, input.expected_result, input.assetContext?.expectedResult);
  const competitor = first(input.competitorOrEntity, input.assetContext?.competitor, comparisonTarget(opportunityTitle));
  const rawTargetLocation = targetLocationForOutput(input.targetLocation);
  const assetTargetUrl = input.assetFocused === true
    ? safeAbsoluteHttpUrl(input.targetAsset || rawTargetLocation.url)
    : "";
  const targetLocation = input.assetFocused === true && assetTargetUrl
    ? {
      ...rawTargetLocation,
      mode: "existing_asset",
      url: assetTargetUrl,
      path: (() => {
        try { return new URL(assetTargetUrl).pathname; } catch { return rawTargetLocation.path || null; }
      })(),
    }
    : rawTargetLocation;
  const resolvedLocation = targetLocationLabel(targetLocation);
  const rawTargetAsset = first(input.targetAsset, input.assetContext?.targetAsset, input.actionBrief?.content_brief?.recommended_asset);
  const normalizedTargetAsset = productArea === "competitive"
    ? normalizeCompetitiveTargetAsset(rawTargetAsset, competitor)
    : rawTargetAsset;
  const candidateSource = targetLocation.evidenceSourcesUsed?.find((value) => ["sitemap_bootstrap", "homepage_link_bootstrap", "operator_configured_target_url"].includes(value));
  const candidateTargetAsset = resolvedLocation && ["candidate_existing_asset", "operator_configured_candidate"].includes(targetLocation.mode)
    ? `${candidateSource === "operator_configured_target_url" ? "Operator-configured target page" : `Candidate page from ${candidateSource === "homepage_link_bootstrap" ? "homepage" : "sitemap"}`}: ${resolvedLocation}`
    : "";
  const targetAsset = resolvedLocation && targetLocation.mode === "existing_asset"
    ? `Recommended page to update: ${resolvedLocation}`
    : targetLocation.mode === "operator_configured_candidate"
      ? normalizedTargetAsset
      : candidateTargetAsset || normalizedTargetAsset;
  const prescription = recommendedAssetForContext({
    targetAsset,
    recommended_asset_type: input.recommended_asset_type,
    recommended_asset_label: input.recommended_asset_label,
    recommended_asset_url: input.recommended_asset_url,
  }, {
    ...input,
    accountName,
    recommendationTitle: opportunityTitle,
    targetLocation,
    assetContext: input.assetContext,
  }, productArea);
  const recommendedTargetAsset = prescription.label || targetAsset;
  const comparisonOpportunity = Boolean(competitor && /\b(compare|comparison|against|vs\.?|proof)\b/i.test(opportunityTitle));
  const prescriptionArea = comparisonOpportunity ? "competitive" : productArea;
  const currentAssetContext = safeCurrentAssetContext(input, {
    targetAsset,
    competitor,
    evidencePattern,
    expectedResult,
    targetLocation: input.targetLocation && typeof input.targetLocation === "object" ? targetLocation : null,
  });
  const suppliedCompetitors = list(input.competitors || input.competitorSignals);
  const competitors = suppliedCompetitors.length ? suppliedCompetitors : list(competitor);
  const recommendedAngle = first(
    input.recommendedAngle,
    input.recommended_angle,
    input.objective,
    input.assetContext?.objective,
    input.actionBrief?.recommended_action,
  );
  const nextStep = first(input.nextStep, input.next_step, input.primaryNextAction, input.primary_next_action, input.recommendedAction, input.recommended_action, input.action, input.actionBrief?.recommended_action);
  const localIntent = prescriptionArea === "local" ? first(input.localIntent, input.local_intent, targetAudience) : "";
  const buyerIntent = prescriptionArea === "competitive" ? first(input.buyerIntent, input.buyer_intent, targetAudience) : "";
  const buyerEmpathy = buyerEmpathyContext({
    productArea: prescriptionArea,
    accountName,
    competitor,
    targetAudience,
    localIntent,
    evidencePattern,
    targetAsset: recommendedTargetAsset,
    expectedResult,
  });
  const evidenceList = proofPoints(input, evidencePattern);
  const evidenceToConfirm = proofPointsToConfirm(input, evidencePattern);
  const evidenceProvenance = Array.isArray(input.evidenceItems)
    ? input.evidenceItems.slice(0, 12).map((item) => ({
      text: text(item?.text),
      sourceType: text(item?.sourceType || item?.source_type, "unknown"),
      reviewStatus: ["reviewed", "unverified", "unknown"].includes(item?.reviewStatus || item?.review_status) ? (item.reviewStatus || item.review_status) : "unknown",
      relation: text(item?.relation, "observation"),
    })).filter((item) => item.text)
    : [];
  const actionLocation = targetLocation.mode === "operator_configured_candidate"
    ? { ...targetLocation, mode: "gap_backed_new_asset" }
    : targetLocation;
  const recommendedActions = prescribedActions({ nextStep, targetLocation: actionLocation, targetAsset: recommendedTargetAsset, competitor, productArea: prescriptionArea });
  const sections = input.sectionOutline?.length ? sectionOutline(input) : sectionOutline({ ...input, productArea: prescriptionArea });
  const suppliedFaqs = list(input.FAQsToAnswer || input.faqsToAnswer || input.faqs);
  const suppliedClaims = list(input.claimsToSupport || input.claims_to_support);
  const suppliedPageNotes = list(input.pageOrSchemaNotes || input.schemaOrPageNotes || input.schema_or_page_notes);
  const pageOrSchemaNotes = suppliedPageNotes.length ? suppliedPageNotes : resolvedLocation && targetLocation.mode === "existing_asset"
    ? [
      `Update the reviewed page at ${resolvedLocation}.`,
      "Add comparison FAQs and matching FAQ schema where appropriate.",
      "Review headings, body copy, and internal links against the opportunity evidence.",
    ].map(text)
    : [
      prescriptionArea === "local" ? "Create a focused local service and location proof asset rather than assuming an existing page is sufficient." : "Create a focused comparison and proof asset rather than assuming an existing page is sufficient.",
      "Define the page path, headings, comparison FAQs, and matching FAQ schema before drafting.",
    ].map(text);
  const implementation = implementationFields({
    input,
    productArea: prescriptionArea,
    targetLocation,
    targetAsset: recommendedTargetAsset,
    competitor,
    targetAudience,
    evidencePattern,
    buyerIntent,
    localIntent,
    expectedResult,
  });
  const output = {
    outputTitle: opportunityTitle || targetAsset ? `${accountName} · ${opportunityTitle || targetAsset}`.slice(0, MAX_TEXT) : "",
    outputType: "CAI Output",
    opportunityTitle,
    targetAsset: recommendedTargetAsset,
    recommended_asset_type: prescription.type || null,
    recommended_asset_url: prescription.url,
    recommended_asset_label: prescription.label || null,
    asset_action: assetActionForType(prescription.type),
    ...(targetLocation.mode === "operator_configured_candidate" ? {
      supporting_context_url: safeAbsoluteHttpUrl(targetLocation.url) || null,
      supporting_context_role: prescription.operatorRole || "supporting_context",
    } : {}),
    operatorTargetContext: targetLocation.mode === "operator_configured_candidate" ? {
      url: safeAbsoluteHttpUrl(targetLocation.url) || null,
      source_method: "operator_configured_target_url",
      target_asset_candidate_mode: targetLocation.mode,
      role: prescription.operatorRole || "supporting_context",
    } : null,
    targetAudience,
    assetSummary: currentAssetContext,
    competitorContext: prescriptionArea === "competitive" ? competitors : [],
    evidenceSummary: evidencePattern,
    recommendedActions,
    proofPoints: evidenceList,
    proofPointsToConfirm: evidenceToConfirm,
    proofPointProvenance: evidenceProvenance,
    targetLocation,
    assetContext: safeAssetContext(input, recommendedTargetAsset, competitor, evidencePattern, expectedResult, recommendedAngle),
    buyerEmpathyContext: buyerEmpathy,
    buyerIntent: buyerIntent || null,
    localIntent: localIntent || null,
    evidencePattern,
    currentAssetContext,
    assetGap: evidencePattern,
    recommendedAngle,
    recommendedStructure: sections,
    sectionOutline: sections,
    FAQsToAnswer: suppliedFaqs.length ? suppliedFaqs : comparisonFaqs({ accountName, competitor, buyerIntent }),
    claimsToSupport: suppliedClaims.length ? suppliedClaims : comparisonClaims({ accountName, competitor, buyerIntent }),
    proofPointsNeeded: evidenceToConfirm,
    pageOrSchemaNotes,
    implementationSummary: implementation.implementationSummary,
    recommendedPageUpdates: implementation.recommendedPageUpdates,
    headingRecommendations: implementation.headingRecommendations,
    faqSchemaRecommendations: implementation.faqSchemaRecommendations,
    validationNextStep: implementation.validationNextStep,
    expectedResult,
    nextStep,
    comparisonContext: prescriptionArea === "competitive" ? competitors : [],
  };

  const required = [
    ["opportunityTitle", output.opportunityTitle],
    ["targetAsset", output.targetAsset],
    ["targetAudience", output.targetAudience],
    ["evidencePattern", output.evidencePattern],
    ["currentAssetContext", output.currentAssetContext],
    ["recommendedAngle", output.recommendedAngle],
    ["expectedResult", output.expectedResult],
    ["nextStep", output.nextStep],
  ];
  const failures = required.filter(([, value]) => !value).map(([field]) => `missing_${field}`);
  const contractFailures = [];
  if (!output.outputTitle) contractFailures.push("missing_output_title");
  if (!output.assetSummary) contractFailures.push("missing_asset_summary");
  if (prescriptionArea === "competitive" && !input.assetFocused && !output.competitorContext.length) contractFailures.push("missing_competitor_context");
  if (!prescription.type || !prescription.label) contractFailures.push("missing_recommended_asset");
  if (!output.recommendedAngle) contractFailures.push("missing_recommended_asset_rationale");
  if (!hasMeaningfulImplementationDirection(output)) contractFailures.push("missing_asset_implementation_brief");
  const recommendedActionPattern = ["new_page", "comparison_page", "proof_page"].includes(prescription.type)
    ? /\b(create|build|develop|write|produce|draft)\b/i
    : /\b(improve|update|strengthen|clarify|revise|add|refresh)\b/i;
  if (!output.recommendedActions.some((action) => recommendedActionPattern.test(action))) contractFailures.push("missing_recommended_asset_action");
  if (!output.evidenceSummary) contractFailures.push("missing_evidence_summary");
  if (!output.recommendedActions.length) contractFailures.push("missing_recommended_actions");
  if (!output.proofPoints.length && !output.proofPointsToConfirm.length) contractFailures.push("missing_proof_points");
  if (!output.buyerEmpathyContext) contractFailures.push("missing_buyer_empathy_context");
  if (!output.validationNextStep) contractFailures.push("missing_validation_next_step");
  if (productArea === "competitive" && ["candidate_existing_asset", "operator_configured_candidate"].includes(input.targetLocation?.mode) && (!output.targetLocation || output.targetLocation.mode !== input.targetLocation.mode || (!output.targetLocation.url && !output.targetLocation.path))) contractFailures.push("missing_candidate_asset_context");
  if (prescriptionArea === "competitive" && /local\s+(?:service|market)|service[- ]area|service area|local visibility/i.test(String(output.targetAsset || ""))) contractFailures.push("weak_asset_specificity");
  if (/(?:guaranteed|best|#1|number one|leading provider|always wins)/i.test(JSON.stringify({ claimsToSupport: output.claimsToSupport, recommendedActions: output.recommendedActions }))) contractFailures.push("unsupported_claims");
  if (prescriptionArea === "competitive" && (!output.opportunityTitle || !output.targetAsset || !output.targetAudience || !output.expectedResult)) {
    contractFailures.push("insufficient_specificity");
  }
  failures.push(...contractFailures);
  if (!output.opportunityTitle && !output.recommendedAngle) failures.push("missing_opportunity_context");
  if (!output.currentAssetContext) failures.push("missing_current_asset_context");
  if (!output.evidencePattern) failures.push("missing_evidence_pattern");
  if (!output.targetAsset || /^(asset|target asset|asset unavailable|not available)$/i.test(output.targetAsset)) failures.push("generic_target_asset");
  if (UNSAFE_OUTPUT.test(JSON.stringify(output))) failures.push("unsafe_customer_output_text");
  if (output.evidencePattern && output.expectedResult && output.evidencePattern.toLowerCase() === output.expectedResult.toLowerCase()) {
    failures.push("evidence_matches_expected_result");
    failures.push("duplicate_evidence_expected_result");
  }
  if (output.targetAudience && /^(competitor|entity|brand|company|organization|business)$/i.test(output.targetAudience)) failures.push("audience_needs_use_case");
  if (output.sectionOutline.length < 3) failures.push("outline_needs_specific_structure");
  if (output.recommendedAngle && output.nextStep && output.recommendedAngle.toLowerCase() === output.nextStep.toLowerCase()) failures.push("recommendation_needs_value");

  const uniqueFailures = Array.from(new Set(failures));
  const reasonCode = contractFailures[0] || (uniqueFailures.length ? "quality_gate_failed" : null);
  const qualityFailureDetails = describeCaiOutputQualityFailures(uniqueFailures);
  const normalizedReasonCode = classifyCaiOutputQualityFailure(uniqueFailures, reasonCode);
  return uniqueFailures.length
    ? { status: "needs_review", reason: "quality_gate_failed", reasonCode: normalizedReasonCode, qualityFailures: uniqueFailures, qualityFailureDetails, output: null, message: "CAI needs stronger reviewed inputs for this opportunity." }
    : { status: "ready", reason: "quality_gate_passed", reasonCode: null, qualityFailures: [], output };
}

/**
 * Build a narrowly scoped fallback from an authenticated, structured local
 * opportunity snapshot after a repeated provider timeout. This still passes
 * both the builder contract and final customer-output quality gate; incomplete
 * evidence remains needs_review.
 */
export function buildCaiDeterministicStructuredOpportunityFallback(input = {}) {
  const location = targetLocationForOutput(input.targetLocation);
  const targetUrl = safeAbsoluteHttpUrl(location.url);
  const hasOperatorTarget = input.productArea === "local"
    && location.mode === "operator_configured_candidate"
    && location.evidenceSourcesUsed.includes("operator_configured_target_url")
    && Boolean(targetUrl);
  const confidence = Number(input.confidence);
  const reviewedEvidence = Array.isArray(input.reviewedSignals) ? input.reviewedSignals.filter(Boolean) : [];
  const minimumStructuredContext = [
    input.accountName,
    input.opportunityTitle || input.recommendationTitle,
    input.recommendationType,
    input.competitorOrEntity || input.targetEntity,
    input.recommendedOutput,
    input.targetAudience,
    input.evidencePattern,
    input.expectedResult,
    input.recommendedAction || input.nextStep,
    input.currentAssetContext,
  ].every((value) => Boolean(text(value)))
    && Number.isFinite(confidence)
    && confidence > 0
    && confidence <= 1
    && reviewedEvidence.length > 0;

  if (!hasOperatorTarget || !minimumStructuredContext) {
    const qualityFailures = [];
    if (!hasOperatorTarget) qualityFailures.push("missing_supporting_context_provenance");
    if (!minimumStructuredContext) qualityFailures.push("insufficient_specificity");
    return {
      status: "needs_review",
      reason: "structured_opportunity_context_insufficient",
      reasonCode: qualityFailures[0],
      qualityFailures,
      qualityFailureDetails: describeCaiOutputQualityFailures(qualityFailures),
      output: null,
    };
  }

  const built = buildCaiOpportunityOutput(input);
  if (built.status !== "ready" || !built.output) return built;
  const output = normalizeCaiOpportunityOutputForContext(built.output, input, "local");
  const quality = validateCaiOpportunityOutput(output, { ...input, productArea: "local" });
  if (quality.status !== "ready") return quality;
  return {
    status: "ready",
    reason: "deterministic_structured_opportunity_ready",
    reasonCode: null,
    qualityFailures: [],
    output: quality.output,
    synthesisSource: "deterministic_structured_opportunity",
    implementationBriefSource: "deterministic_context_fallback",
  };
}

/**
 * Build a bounded existing-page optimization brief from reviewed asset-row
 * evidence when the provider omits or mis-shapes the asset prescription.
 * This is only eligible for an explicitly selected Asset opportunity with a
 * safe page URL; it never invents a page or claims a new asset is existing.
 */
export function buildCaiDeterministicAssetContextFallback(input = {}) {
  const sourceLocation = targetLocationForOutput(input.targetLocation);
  const targetUrl = safeAbsoluteHttpUrl(input.targetAsset || sourceLocation.url);
  if (!input.assetFocused || !targetUrl) {
    const qualityFailures = ["invalid_recommended_asset"];
    return {
      status: "needs_review",
      reason: "asset_context_insufficient",
      reasonCode: qualityFailures[0],
      qualityFailures,
      qualityFailureDetails: describeCaiOutputQualityFailures(qualityFailures),
      output: null,
    };
  }

  const targetLocation = {
    ...sourceLocation,
    mode: "existing_asset",
    url: targetUrl,
    path: (() => {
      try { return new URL(targetUrl).pathname; } catch { return sourceLocation.path || null; }
    })(),
  };
  const built = buildCaiOpportunityOutput({
    ...input,
    assetFocused: true,
    targetAsset: targetUrl,
    targetLocation,
    currentAssetContext: input.currentAssetContext || `Reviewed account asset selected for optimization: ${targetUrl}.`,
  });
  if (built.status !== "ready" || !built.output) return built;

  const output = normalizeCaiOpportunityOutputForContext(built.output, {
    ...input,
    assetFocused: true,
    targetAsset: targetUrl,
    targetLocation,
  }, input.productArea);
  const quality = validateCaiOpportunityOutput(output, {
    ...input,
    assetFocused: true,
    targetAsset: targetUrl,
    targetLocation,
  });
  return quality.status === "ready"
    ? {
      status: "ready",
      reason: "deterministic_asset_context_ready",
      reasonCode: null,
      qualityFailures: [],
      output: quality.output,
      implementationBriefSource: "deterministic_context_fallback",
    }
    : quality;
}

const QUALITY_FAILURE_COPY = Object.freeze({
  missing_output_title: ["outputTitle", "The generated brief did not include a clear title.", "A concise title tied to the reviewed opportunity.", "No safe title was available."],
  missing_targetAsset: ["targetAsset", "The generated brief did not name a recommended asset.", "A customer-safe label for the recommended asset.", "No recommended asset label was available."],
  missing_targetAudience: ["targetAudience", "The generated brief did not identify who the asset is for.", "A reviewed audience or decision context.", "No safe audience was available."],
  missing_expectedResult: ["expectedResult", "The generated brief did not identify the intended result.", "A concrete result tied to the reviewed opportunity.", "No safe expected result was available."],
  missing_nextStep: ["nextStep", "The generated brief did not identify the next action.", "A specific next step grounded in the reviewed opportunity.", "No safe next step was available."],
  missing_opportunity_context: ["opportunityTitle", "The generated brief did not retain the reviewed opportunity context.", "A clear opportunity title or recommendation.", "No safe opportunity context was available."],
  missing_current_asset_context: ["currentAssetContext", "The generated brief did not include current asset context.", "A bounded account asset or target-page context summary.", "No safe current asset context was available."],
  missing_evidence_pattern: ["evidencePattern", "The generated brief did not identify the evidence pattern.", "A reviewed signal that explains why this opportunity was selected.", "No safe evidence pattern was available."],
  missing_asset_summary: ["assetSummary", "The generated brief did not summarize the asset context.", "A concise summary of the relevant page or asset context.", "No safe asset summary was available."],
  missing_competitor_context: ["competitorContext", "The generated brief did not identify the relevant competitor context.", "Reviewed competitor or entity context for this comparison.", "No safe competitor context was available."],
  missing_evidence_summary: ["evidenceSummary", "The generated brief did not summarize the evidence behind the opportunity.", "A concise summary grounded in reviewed evidence.", "No safe evidence summary was available."],
  missing_recommended_actions: ["recommendedActions", "The generated brief did not provide a usable next action.", "At least one specific action based on the reviewed opportunity.", "No safe recommended action was available."],
  missing_proof_points: ["proofPoints", "The generated brief did not identify reviewed proof points.", "One or more proof points supported by available evidence.", "No safe proof points were available."],
  missing_buyer_empathy_context: ["buyerEmpathyContext", "The generated brief did not explain the buyer decision context.", "A practical buyer concern grounded in the opportunity evidence.", "No supported buyer context was available."],
  missing_validation_next_step: ["validationNextStep", "The generated brief did not include a validation next step.", "A clear way to check whether the recommended work addresses the signal.", "No safe validation step was available."],
  missing_recommended_asset: ["recommended_asset", "The generated brief did not identify a customer-safe recommended asset.", "CAI output must identify whether to improve an existing asset or create a new asset.", "No reviewed asset recommendation was available."],
  missing_recommended_asset_action: ["recommendedActions", "The generated brief did not say what to do with the recommended asset.", "A clear action to create a new asset or improve an existing one.", "No action aligned to the recommended asset was available."],
  missing_supporting_context_provenance: ["supporting_context_url", "The configured page was not clearly separated from the recommended asset.", "The operator-configured URL and its supporting-context role, separate from the prescribed asset.", "Supporting-page provenance was missing or did not match the configured page."],
  missing_recommended_asset_rationale: ["recommendedAngle", "The generated brief did not explain why this asset is the right next step.", "A concise rationale grounded in the opportunity evidence and intended result.", "No asset-specific rationale was available."],
  missing_asset_implementation_brief: ["implementationSummary", "The generated brief did not explain what the recommended asset should include.", "A concise, customer-safe implementation brief for the recommended asset.", "No implementation brief was available."],
  invalid_recommended_asset: ["recommended_asset", "The generated brief did not safely ground its recommended page.", "A recommended page URL must match an account-scoped existing asset; new-asset prescriptions should not claim an existing URL.", "The URL did not match a reviewed account asset."],
  operator_target_supporting_context_only: ["recommendedActions", "The selected operator page is supporting site context, not the prescribed asset.", "Keep the configured page as context and recommend a separately identified asset unless the same existing page is explicitly justified.", "The output treated the supporting page as the recommended work target."],
  recommended_action_conflicts_with_supporting_context: ["recommendedActions", "The recommended action treats a supporting page as the work target.", "Keep the configured page as context and direct the action to the separately named recommended asset.", "The action directs work to the supporting page instead of the recommended asset."],
  generic_target_asset: ["recommended_asset_label", "The generated brief used a generic asset description.", "A specific customer-safe description of the page, section, or asset.", "The asset label did not identify a concrete recommendation."],
  evidence_matches_expected_result: ["evidenceSummary", "The evidence summary repeated the intended result instead of explaining the signal.", "A distinct reviewed evidence pattern and expected result.", "Evidence and expected result were not distinguishable."],
  duplicate_evidence_expected_result: ["evidenceSummary", "The evidence summary and intended result were duplicates.", "Separate evidence and intended-result statements.", "The same information was used for both fields."],
  audience_needs_use_case: ["targetAudience", "The audience description did not identify a buyer or use case.", "A meaningful audience or decision context.", "The audience was described only as an entity or organization."],
  outline_needs_specific_structure: ["sectionOutline", "The brief did not include a sufficiently useful content structure.", "At least three relevant sections for the recommended asset.", "The outline was too limited or not specific."],
  recommendation_needs_value: ["recommendedAngle", "The recommendation repeated the next step without adding direction.", "An angle that explains the value of the next step.", "The recommendation and next step were duplicative."],
  output_type_mismatch: ["outputType", "The generated result did not identify itself as a CAI Output.", "The expected CAI Output type.", "The output type did not match the contract."],
  weak_asset_specificity: ["recommended_asset_label", "The recommended asset was not specific enough for this opportunity.", "A concrete asset tied to the reviewed opportunity and entity context.", "The asset label was too broad."],
  unsupported_claims: ["claimsToSupport", "The generated brief included a claim that needs stronger evidence.", "Claims limited to supported, reviewable statements.", "One or more claims were withheld for review."],
  missing_candidate_asset_context: ["targetLocation", "The selected page candidate was not represented clearly.", "A safe target-page reference with its candidate provenance.", "The selected candidate could not be confirmed in the output."],
  selected_competitor_mismatch: ["competitorContext", "The generated brief did not stay tied to the selected competitor.", "The selected competitor or entity from the opportunity context.", "The output referenced a different comparison target."],
  missing_reviewed_asset_evidence: ["proofPoints", "The selected asset does not have reviewed evidence that can support a completed brief.", "Reviewed evidence tied to the selected asset objective; candidate pages remain supporting context only.", "The available asset evidence was not reviewed."],
  asset_action_mismatch: ["asset_action", "The generated brief changed an existing-asset opportunity into a new-asset recommendation.", "An improve-existing-asset action tied to the selected reviewed page.", "The output action did not match the selected asset objective."],
  self_comparison: ["competitorContext", "The generated brief compared the customer with itself.", "A distinct competitor or a page-specific optimization grounded in the selected asset.", "The comparison target was not distinct from the customer."],
  asset_objective_mismatch: ["recommendedActions", "The generated brief did not address the selected asset objective.", "Actions that use the selected page's reviewed optimization evidence.", "The output did not contain enough of the selected objective's specific terms."],
  unreviewed_sitemap_proof: ["proofPoints", "The generated brief presented an unreviewed sitemap candidate as proof.", "Sitemap candidates may provide context but cannot be treated as verified proof.", "The candidate was withheld from completed output."],
  unrelated_asset_target: ["recommended_asset", "The generated brief referenced an unrelated asset.", "The selected account asset URL and its reviewed optimization objective.", "A URL did not match the selected asset context."],
  unreviewed_evidence_as_proof: ["proofPoints", "The generated brief presented an observed signal as verified proof.", "Keep observed visibility evidence separate from proof points that still need confirmation.", "The observation was withheld from verified proof."],
  missing_entity_action: ["entity_action", "The entity brief did not define a reviewed entity action.", "An audit or update action for the primary entity foundation.", "No safe entity action was available."],
  missing_primary_entity_name: ["primary_entity_name", "The entity brief did not identify the primary entity.", "A reviewed primary account or entity name.", "No safe primary entity name was available."],
  missing_entity_domain: ["primary_entity_domain", "The entity brief did not identify a reviewed domain or homepage.", "A reviewed account domain or homepage for entity work.", "No safe domain or homepage was available."],
  missing_entity_fields_to_confirm: ["entity_fields_to_confirm", "The entity brief did not identify the entity fields to confirm.", "Reviewed identity, domain, category, or alias fields to check.", "No entity fields to confirm were available."],
  missing_brand_name_consistency: ["brand_name_consistency_checks", "The entity brief did not include brand and name consistency checks.", "Checks that keep the entity name, domain, and references aligned.", "No safe consistency checks were available."],
  missing_entity_fields_to_review: ["entity_fields_to_review", "The entity brief did not identify the entity fields to review.", "Category, products/services, market, or homepage fields grounded in account context.", "No entity review fields were available."],
  missing_entity_implementation_summary: ["implementationSummary", "The entity brief did not explain how to apply the foundation update.", "A bounded implementation summary for entity and account review.", "No entity implementation summary was available."],
  missing_entity_validation_next_step: ["validationNextStep", "The entity brief did not include a validation next step.", "A clear way to re-check entity clarity after review.", "No entity validation step was available."],
  missing_reviewed_entity_inputs: ["reviewed_entity_inputs", "The entity brief did not identify reviewed entity inputs.", "Reviewed account fields, run evidence, or account-provided Brand Guidance.", "No reviewed entity inputs were available."],
  missing_entity_specific_finding: ["entity_findings", "The entity brief did not identify a specific account finding or missing reviewed input.", "At least one account-specific finding or a concrete missing-context recommendation.", "The output contained only a generic entity checklist."],
  unsafe_customer_output_text: ["customerOutput", "The generated content did not pass customer-safety review.", "Customer-safe language without unsupported or internal content.", "The content was withheld; unsafe text is not shown."],
  insufficient_specificity: ["opportunityContext", "The generated brief lacked enough specific opportunity detail.", "A clear audience, evidence pattern, and expected result.", "The available output did not meet the specificity requirement."],
  malformed_provider_output: ["output", "The generated result was not in a reviewable format.", "A structured CAI output matching the expected contract.", "The result could not be safely summarized."],
});

export function classifyCaiOutputQualityFailure(failures = [], fallback = "quality_gate_failed") {
  const listOfFailures = Array.isArray(failures) ? failures : [];
  if (listOfFailures.includes("unsafe_customer_output_text")) return "provider_output_failed_customer_safety_contract";
  const entityFailure = [
    "missing_entity_action",
    "missing_primary_entity_name",
    "missing_entity_domain",
    "missing_entity_fields_to_confirm",
    "missing_brand_name_consistency",
    "missing_entity_fields_to_review",
    "missing_entity_implementation_summary",
    "missing_entity_validation_next_step",
    "missing_reviewed_entity_inputs",
    "missing_entity_specific_finding",
    "entity_context_insufficient",
  ].find((failure) => listOfFailures.includes(failure));
  if (entityFailure) return entityFailure;
  if (listOfFailures.includes("missing_recommended_asset") || listOfFailures.includes("invalid_recommended_asset")) return "provider_output_missing_recommended_asset";
  if (listOfFailures.includes("selected_competitor_mismatch")) return "cai_output_contract_failed";
  if (listOfFailures.includes("asset_action_mismatch") || listOfFailures.includes("self_comparison") || listOfFailures.includes("asset_objective_mismatch") || listOfFailures.includes("unreviewed_sitemap_proof") || listOfFailures.includes("unrelated_asset_target")) return "cai_output_contract_failed";
  if (listOfFailures.includes("recommended_action_conflicts_with_supporting_context")) return "recommended_action_conflicts_with_supporting_context";
  if (listOfFailures.includes("operator_target_supporting_context_only")) return "operator_target_supporting_context_only";
  if (listOfFailures.includes("missing_candidate_asset_context")) return "operator_configured_target_requires_review";
  if (listOfFailures.length) return "cai_output_contract_failed";
  return fallback;
}

export function describeCaiOutputQualityFailures(failures = []) {
  const listOfFailures = Array.isArray(failures) ? failures : [];
  return Array.from(new Set(listOfFailures)).slice(0, 12).map((failure) => {
    const code = typeof failure === "string" ? failure : "quality_gate_failed";
    const [field, customerSafeSummary, expectedShapeOrRequirement, actualSafeSummary] = QUALITY_FAILURE_COPY[code]
      || ["output", "The generated brief did not meet a required quality check.", "A complete CAI output that meets the reviewed quality contract.", "A safe field-level summary was not available."];
    return {
      code,
      field,
      severity: "blocking",
      customer_safe_summary: customerSafeSummary,
      expected_shape_or_requirement: expectedShapeOrRequirement,
      actual_safe_summary: actualSafeSummary,
    };
  });
}

export function validateEntityCaiOpportunityOutput(providerOutput, input = {}) {
  const output = providerOutput && typeof providerOutput === "object" && !Array.isArray(providerOutput)
    ? providerOutput
    : null;
  if (!output) {
    const qualityFailures = ["malformed_provider_output"];
    return { status: "needs_review", reason: "entity_output_contract_failed", reasonCode: "malformed_provider_output", qualityFailures, qualityFailureDetails: describeCaiOutputQualityFailures(qualityFailures), output: null };
  }
  const entityContext = input.entityContext && typeof input.entityContext === "object" ? input.entityContext : {};
  const primaryEntityName = first(output.primary_entity_name, output.primaryEntityName, entityContext.entityName, input.accountName, entityContext.accountName);
  const primaryEntityDomain = first(output.primary_entity_domain, output.primaryEntityDomain, output.primary_entity_homepage, output.primaryEntityHomepage, entityContext.websiteDomain, entityContext.websiteUrl, input.websiteDomain, input.websiteUrl);
  const entityAction = first(output.entity_action, output.entityAction);
  const fieldsToConfirm = list(output.entity_fields_to_confirm || output.entityFieldsToConfirm);
  const consistencyChecks = list(output.brand_name_consistency_checks || output.brandNameConsistencyChecks);
  const fieldsToReview = list(output.entity_fields_to_review || output.entityFieldsToReview);
  const reviewedInputs = list(output.reviewed_entity_inputs || output.reviewedEntityInputs);
  const entityFindings = list(output.entity_findings || output.entityFindings || output.what_toren_found || output.whatTorenFound);
  const missingContextRecommendation = first(output.missing_context_recommendation, output.missingContextRecommendation);
  const implementationSummary = first(output.implementationSummary, output.implementation_summary);
  const validationNextStep = first(output.validationNextStep, output.validation_next_step);
  const failures = [];
  if (output.outputType !== "CAI Output") failures.push("output_type_mismatch");
  if (!entityAction || !/^(audit|update)_entity_foundation$/i.test(entityAction)) failures.push("missing_entity_action");
  if (!primaryEntityName) failures.push("missing_primary_entity_name");
  if (!primaryEntityDomain) failures.push("missing_entity_domain");
  if (!fieldsToConfirm.length) failures.push("missing_entity_fields_to_confirm");
  if (!consistencyChecks.length) failures.push("missing_brand_name_consistency");
  if (!fieldsToReview.length) failures.push("missing_entity_fields_to_review");
  if (!implementationSummary) failures.push("missing_entity_implementation_summary");
  if (!validationNextStep) failures.push("missing_entity_validation_next_step");
  // Brand Guidance enriches an entity brief but is not a hard dependency.
  // Fields to confirm and the reviewed account/Market Profile context are
  // sufficient when they produce a safe, actionable entity contract.
  const specificReviewedInputs = reviewedInputs.filter((item) => !looksLikeInternalEntityLabel(item)
    && !/^(?:reviewed entity foundation signal|primary (?:entity|business) name|canonical domain|website domain)$/i.test(item));
  const meaningfulEntityFindings = entityFindings.filter((item) => specificEntityEvidence(item));
  const contextHasSpecificEntityInput = Boolean(
    entityContext.accountCategory
      || entityContext.category
      || entityContext.market
      || entityContext.productsServices
      || entityContext.productServiceLanguage
      || hasMarketProfile(entityContext.marketProfile)
      || hasBrandGuidance(entityContext.brandGuidance),
  );
  if (!meaningfulEntityFindings.length && !specificReviewedInputs.length && !missingContextRecommendation && !contextHasSpecificEntityInput) failures.push("missing_entity_specific_finding");
  const safetyOutput = { ...output, entity_brief_source: undefined, entityBriefSource: undefined, entity_context_sources: undefined, entityContextSources: undefined };
  if (UNSAFE_OUTPUT.test(JSON.stringify(safetyOutput))) failures.push("unsafe_customer_output_text");
  const entityCustomerText = JSON.stringify([
    output.outputTitle,
    output.targetAudience,
    output.evidencePattern,
    output.evidenceSummary,
    output.assetSummary,
    output.implementationSummary,
    output.recommendedActions,
    output.entity_fields_to_confirm,
    output.entity_fields_to_review,
    output.reviewed_entity_inputs,
    output.entity_findings,
    output.what_toren_found,
    output.recommended_entity_update,
    output.where_to_apply,
    output.missing_context_recommendation,
    output.expectedResult,
    output.targetAsset,
    output.localIntent,
    output.local_intent,
    output.buyerIntent,
    output.buyer_intent,
    output.entityContext,
  ]);
  if (/(?:identity[_ -]clarity|alternatives?[_ -]replacements?|local[_ -]comparison)|\b(?:local|buyer)\s+intent\b|\b(?:weak|absent)\b/i.test(entityCustomerText)) {
    failures.push("unsafe_customer_output_text");
  }
  const uniqueFailures = Array.from(new Set(failures));
  const reasonCode = uniqueFailures.length ? classifyCaiOutputQualityFailure(uniqueFailures, "entity_context_insufficient") : null;
  const qualityFailureDetails = describeCaiOutputQualityFailures(uniqueFailures);
  return uniqueFailures.length
    ? { status: "needs_review", reason: "entity_output_contract_failed", reasonCode, qualityFailures: uniqueFailures, qualityFailureDetails, output: null }
    : { status: "ready", reason: "quality_gate_passed", reasonCode: null, qualityFailures: [], output };
}

// Validate a provider-composed CAI Output without rebuilding it from the
// deterministic evidence packet. The packet builder remains responsible for
// context assembly; this contract is the final customer-output gate.
export function validateCaiOpportunityOutput(providerOutput, input = {}) {
  if (inspectCaiOutputSafety(providerOutput).categories.includes("malformed_provider_markup")) {
    const qualityFailures = ["unsafe_customer_output_text"];
    return { status: "needs_review", reason: "quality_gate_failed", reasonCode: "provider_output_failed_customer_safety_contract", qualityFailures, qualityFailureDetails: describeCaiOutputQualityFailures(qualityFailures), output: null };
  }
  const output = providerOutput && typeof providerOutput === "object" && !Array.isArray(providerOutput)
    ? providerOutput
    : null;
  if (!output) return { status: "needs_review", reason: "quality_gate_failed", reasonCode: "malformed_provider_output", qualityFailures: ["malformed_provider_output"], qualityFailureDetails: describeCaiOutputQualityFailures(["malformed_provider_output"]), output: null };
  const productArea = input.productArea === "local" ? "local" : "competitive";
  const assetFocused = input.assetFocused === true;
  if (assetFocused && input.entityFocused === true) {
    const qualityFailures = ["output_contract_context_conflict"];
    return { status: "needs_review", reason: "quality_gate_failed", reasonCode: "output_contract_context_conflict", qualityFailures, qualityFailureDetails: describeCaiOutputQualityFailures(qualityFailures), output: null };
  }
  // Only trusted context selects the contract. Provider-authored wording must
  // neither upgrade an Assets brief to Entity nor downgrade a true Entity brief.
  const entityFocused = !assetFocused && (input.entityFocused === true || isEntityFocusedOpportunity(input));
  if (entityFocused) return validateEntityCaiOpportunityOutput(output, { ...input, entityFocused: true });
  const required = [
    ["missing_output_title", output.outputTitle],
    ["missing_asset_summary", output.assetSummary],
    ["missing_recommended_asset", output.recommended_asset_type && output.recommended_asset_label],
    ["missing_recommended_asset_action", output.asset_action],
    ["targetAsset", output.targetAsset],
    ["targetAudience", output.targetAudience],
    ["missing_evidence_summary", output.evidenceSummary],
    ["missing_recommended_actions", Array.isArray(output.recommendedActions) && output.recommendedActions.length],
    ["missing_proof_points", (Array.isArray(output.proofPoints) && output.proofPoints.length) || (Array.isArray(output.proofPointsToConfirm) && output.proofPointsToConfirm.length)],
    ["missing_buyer_empathy_context", output.buyerEmpathyContext],
    ["expectedResult", output.expectedResult],
    ["nextStep", output.nextStep],
  ];
  const failures = required.filter(([, value]) => !value).map(([field]) => field.startsWith("missing_") ? field : `missing_${field}`);
  if (output.outputType !== "CAI Output") failures.push("output_type_mismatch");
  const recommendedAssetType = output.recommended_asset_type;
  if (!RECOMMENDED_ASSET_TYPES.has(recommendedAssetType)) failures.push("missing_recommended_asset");
  const assetAction = assetActionForType(recommendedAssetType);
  const expectedAction = assetAction;
  const actionPattern = expectedAction === "create_new_asset" ? /\b(create|build|develop|write|produce|draft)\b/i : /\b(improve|update|strengthen|clarify|revise|add|refresh)\b/i;
  if (!assetAction || output.asset_action !== expectedAction || !Array.isArray(output.recommendedActions) || !output.recommendedActions.some((action) => typeof action === "string" && actionPattern.test(action))) {
    failures.push("missing_recommended_asset_action");
  }
  if (!output.recommendedAngle) failures.push("missing_recommended_asset_rationale");
  if (!hasMeaningfulImplementationDirection(output)) failures.push("missing_asset_implementation_brief");
  if (recommendedAssetType === "existing_page") {
    const reviewedUrl = assetFocused
      ? safeAbsoluteHttpUrl(input.targetAsset || input.targetLocation?.url)
      : input.targetLocation?.mode === "existing_asset"
      ? safeAbsoluteHttpUrl(input.targetLocation.url)
      : input.targetLocation?.mode === "operator_configured_candidate" && output.operatorTargetContext?.role === "candidate_existing_asset"
        ? safeAbsoluteHttpUrl(input.targetLocation.url)
        : "";
    if (!reviewedUrl || safeAbsoluteHttpUrl(output.recommended_asset_url) !== reviewedUrl) failures.push("invalid_recommended_asset");
  } else if (output.recommended_asset_url) {
    failures.push("invalid_recommended_asset");
  }
  if (productArea === "competitive" && !entityFocused && !assetFocused && (!Array.isArray(output.competitorContext) || !output.competitorContext.length)) failures.push("missing_competitor_context");
  if (productArea === "competitive" && !entityFocused && !assetFocused && (!output.evidencePattern || !output.currentAssetContext)) failures.push("insufficient_specificity");
  if (productArea === "competitive" && !entityFocused && !assetFocused && !hasSelectedCompetitiveEntity(output, input)) failures.push("selected_competitor_mismatch");
  if (assetFocused) {
    const targetUrl = safeAbsoluteHttpUrl(input.targetAsset || input.targetLocation?.url);
    if (!targetUrl || output.recommended_asset_type !== "existing_page" || safeAbsoluteHttpUrl(output.recommended_asset_url) !== targetUrl) failures.push("invalid_recommended_asset");
    if (output.asset_action !== "improve_existing_asset") failures.push("missing_recommended_asset_action");
    const targetContext = input.targetLocation && typeof input.targetLocation === "object" ? input.targetLocation : {};
    const targetProvenance = [
      ...(Array.isArray(targetContext.evidenceSourcesUsed) ? targetContext.evidenceSourcesUsed : []),
      targetContext.fallbackReason,
      targetContext.reviewStatus,
    ].filter(Boolean).join(" ");
    if (/(?:sitemap|homepage|operator[-_ ]configured|not a reviewed asset|unverified)/i.test(targetProvenance)
      && !/reviewed/i.test(targetContext.reviewStatus || "")) {
      failures.push("invalid_recommended_asset");
    }
    if (output.asset_action === "create_new_asset") failures.push("asset_action_mismatch");
    if (hasSelfComparison(output, input)) failures.push("self_comparison");
    if (!hasAssetObjectiveAlignment(output, input)) failures.push("asset_objective_mismatch");
    if (hasUnreviewedSitemapProof(output, input)) failures.push("unreviewed_sitemap_proof");
    if (Array.isArray(input.evidenceItems)
      && !input.evidenceItems.some((item) => item?.reviewStatus === "reviewed" || ["reviewed_observation", "reviewed_proof"].includes(item?.sourceType || item?.source_type))) {
      failures.push("missing_reviewed_asset_evidence");
    }
    const supportingUrls = new Set((Array.isArray(input.assetCandidates) ? input.assetCandidates : [])
      .filter(item => item?.review_status === "reviewed" && item?.evidence_role === "supporting_context")
      .map(item => safeAbsoluteHttpUrl(item.asset_url)).filter(Boolean));
    const referenceFields = new Set(["evidenceSummary", "proofPoints", "proofPointsToConfirm", "claimsToSupport"]);
    const hasUnrelatedUrl = Object.entries(output).some(([field, value]) => {
      if (!referenceFields.has(field)) return false;
      const entries = Array.isArray(value) ? value : [value];
      return entries.some(entry => {
        if (typeof entry !== "string") return false;
        const urls = entry.match(/https?:\/\/[^\s)]+/gi) || [];
        return urls.some(url => {
          const normalized = safeAbsoluteHttpUrl(url.replace(/[.,;!?]+$/, ""));
          if (!normalized || normalized === targetUrl) return false;
          return !supportingUrls.has(normalized)
            || !/\b(?:supporting context|supporting reference|reference|source)\b/i.test(entry)
            || /\b(?:update|edit|improve|revise|rewrite|optimize|refresh|replace)\b/i.test(entry);
        });
      });
    });
    const workOutput = Object.fromEntries(Object.entries(output).filter(([field]) => !referenceFields.has(field)));
    const workUrls = outputTextForSemanticChecks(workOutput).match(/https?:\/\/[^\s)]+/gi) || [];
    if (targetUrl && (hasUnrelatedUrl || workUrls.some(url => {
      const normalized = safeAbsoluteHttpUrl(url.replace(/[.,;!?]+$/, ""));
      return normalized && normalized !== targetUrl;
    }))) {
      failures.push("unrelated_asset_target");
    }
    const selectedAssetObjective = [input.recommendationTitle, input.opportunityTitle, input.recommendedAction, input.objective].filter(Boolean).join(" ");
    if (output.recommended_asset_type === "existing_page"
      && /\b(?:comparison|proof)\b/i.test(String(output.targetAsset || ""))
      && !/\b(?:comparison|proof)\b/i.test(selectedAssetObjective)
      && !String(output.targetAsset || "").includes(targetUrl || "")) {
      failures.push("unrelated_asset_target");
    }
  }
  if (UNSAFE_OUTPUT.test(JSON.stringify(output))) failures.push("unsafe_customer_output_text");
  if (!entityFocused && hasUnreviewedEvidenceAsProof(output, input)) failures.push("unreviewed_evidence_as_proof");
  if (!assetFocused && !entityFocused && input.targetLocation?.mode === "operator_configured_candidate"
    && output.operatorTargetContext?.role !== "candidate_existing_asset") {
    const operatorUrl = safeAbsoluteHttpUrl(input.targetLocation.url);
    const operatorPath = first(input.targetLocation.path, (() => { try { return new URL(operatorUrl).pathname; } catch { return ""; } })());
    const prescriptionActions = [output.recommendedActions, output.recommendedPageUpdates, output.implementationSummary, output.pageOrSchemaNotes]
      .flat().filter((value) => typeof value === "string").join(" ");
    const operatorReference = [operatorUrl, operatorPath].filter(Boolean).find((reference) => prescriptionActions.includes(reference));
    const directsWorkToSupportingPage = operatorReference && (
      new RegExp(`\\b(update|edit|improve|revise|rewrite|optimize|strengthen|refresh)\\b[^.!?]{0,100}${escapeRegExp(operatorReference)}`, "i").test(prescriptionActions)
      || new RegExp(`${escapeRegExp(operatorReference)}[^.!?]{0,100}\\b(as|into)\\s+(the\\s+)?(recommended|target|comparison|proof)\\s+page\\b`, "i").test(prescriptionActions)
    );
    const hasDistinctNewAsset = ["new_page", "comparison_page", "proof_page"].includes(recommendedAssetType)
      && Boolean(output.recommended_asset_label)
      && !output.recommended_asset_url;
    if (output.supporting_context_role !== "supporting_context"
      || safeAbsoluteHttpUrl(output.supporting_context_url) !== operatorUrl) {
      failures.push("missing_supporting_context_provenance");
    }
    const hasGroundedExistingAsset = recommendedAssetType === "existing_page"
      && Boolean(operatorUrl && safeAbsoluteHttpUrl(output.recommended_asset_url) === operatorUrl);
    if (!hasDistinctNewAsset && !hasGroundedExistingAsset) {
      failures.push("operator_target_supporting_context_only");
    } else if (directsWorkToSupportingPage) {
      failures.push("recommended_action_conflicts_with_supporting_context");
    }
  }
  if (output.targetAsset && /^(asset|target asset|asset unavailable|not available)$/i.test(String(output.targetAsset).trim())) failures.push("generic_target_asset");
  const uniqueFailures = Array.from(new Set(failures));
  const contractFailure = [
    "missing_output_title",
    "missing_asset_summary",
    "missing_competitor_context",
    "missing_evidence_summary",
    "missing_recommended_actions",
    "missing_proof_points",
    "missing_buyer_empathy_context",
    "insufficient_specificity",
    "missing_validation_next_step",
    "missing_candidate_asset_context",
    "weak_asset_specificity",
    "unsupported_claims",
  ].find((failure) => uniqueFailures.includes(failure));
  const qualityFailureDetails = describeCaiOutputQualityFailures(uniqueFailures);
  return uniqueFailures.length
    ? { status: "needs_review", reason: "quality_gate_failed", reasonCode: classifyCaiOutputQualityFailure(uniqueFailures, contractFailure || "quality_gate_failed"), qualityFailures: uniqueFailures, qualityFailureDetails, output: null }
    : { status: "ready", reason: "quality_gate_passed", reasonCode: null, qualityFailures: [], output };
}

// Candidate location and provenance are assembled from account-scoped server
// context. Preserve that context when a provider returns a generic or stale
// target description; this does not fill missing quality fields.
export function normalizeCaiOpportunityOutputForContext(providerOutput, context = {}, productArea = "competitive") {
  if (!providerOutput || typeof providerOutput !== "object" || Array.isArray(providerOutput)) return providerOutput;
  const targetLocation = targetLocationForOutput(context.targetLocation);
  const prescription = recommendedAssetForContext(providerOutput, context, productArea);
  if (context.assetFocused === true) {
    const targetUrl = safeAbsoluteHttpUrl(context.targetAsset || context.targetLocation?.url);
    return {
      ...providerOutput,
      outputType: "CAI Output",
      targetAsset: targetUrl || providerOutput.targetAsset || "Existing asset",
      recommended_asset_type: "existing_page",
      recommended_asset_url: targetUrl || safeAbsoluteHttpUrl(providerOutput.recommended_asset_url) || null,
      recommended_asset_label: providerOutput.recommended_asset_label || targetUrl || "Existing page to improve",
      asset_action: "improve_existing_asset",
      targetLocation: targetLocation.mode === "existing_asset" ? targetLocation : { ...targetLocation, mode: "existing_asset", url: targetUrl },
      currentAssetContext: context.currentAssetContext || providerOutput.currentAssetContext || `Existing account asset selected for optimization: ${targetUrl}`,
      assetSummary: context.currentAssetContext || providerOutput.assetSummary || `Review and improve the existing page at ${targetUrl}.`,
    };
  }
  if (targetLocation.mode === "operator_configured_candidate"
    && targetLocation.evidenceSourcesUsed.includes("operator_configured_target_url")) {
    const operatorTargetContext = {
      url: safeAbsoluteHttpUrl(targetLocation.url) || null,
      source_method: "operator_configured_target_url",
      target_asset_candidate_mode: "operator_configured_candidate",
      role: prescription.operatorRole || "supporting_context",
    };
    const assetContext = providerOutput.assetContext && typeof providerOutput.assetContext === "object" ? providerOutput.assetContext : {};
    return {
      ...providerOutput,
      targetLocation,
      targetAsset: prescription.label || "",
      recommended_asset_type: prescription.type || null,
      recommended_asset_url: prescription.url,
      recommended_asset_label: prescription.label || null,
      asset_action: typeof providerOutput.asset_action === "string" && providerOutput.asset_action.trim()
        ? providerOutput.asset_action.trim()
        : assetActionForType(prescription.type),
      supporting_context_url: operatorTargetContext.url,
      supporting_context_role: operatorTargetContext.role,
      operatorTargetContext,
      currentAssetContext: context.currentAssetContext || providerOutput.currentAssetContext,
      assetSummary: context.currentAssetContext || providerOutput.assetSummary,
      assetContext: {
        ...assetContext,
        mode: prescription.operatorRole === "candidate_existing_asset" ? "existing_asset" : "gap_backed_new_asset",
        targetAsset: prescription.label || null,
        competitor: assetContext.competitor || context.competitorOrEntity || context.targetEntity || null,
        evidencePattern: assetContext.evidencePattern || context.evidencePattern || null,
        expectedResult: assetContext.expectedResult || context.expectedResult || null,
      },
    };
  }
  if (!["candidate_existing_asset", "operator_configured_candidate"].includes(targetLocation.mode)) {
    return {
      ...providerOutput,
      recommended_asset_type: prescription.type || null,
      recommended_asset_label: prescription.label || null,
      recommended_asset_url: prescription.url,
      asset_action: typeof providerOutput.asset_action === "string" && providerOutput.asset_action.trim()
        ? providerOutput.asset_action.trim()
        : assetActionForType(prescription.type),
    };
  }
  const location = targetLocationLabel(targetLocation);
  const source = targetLocation.evidenceSourcesUsed?.find((value) => ["sitemap_bootstrap", "homepage_link_bootstrap", "operator_configured_target_url"].includes(value));
  const candidateLabel = location
    ? `${source === "operator_configured_target_url" ? "Operator-configured target page" : `Candidate page from ${source === "homepage_link_bootstrap" ? "homepage" : "sitemap"}`}: ${location}`
    : "Competitive proof asset supported by candidate pages";
  const assetContext = providerOutput.assetContext && typeof providerOutput.assetContext === "object" ? providerOutput.assetContext : {};
  return {
    ...providerOutput,
    targetLocation,
    targetAsset: productArea === "competitive" ? candidateLabel : (providerOutput.targetAsset || candidateLabel),
    recommended_asset_type: prescription.type || null,
    recommended_asset_url: prescription.url,
    recommended_asset_label: prescription.label || null,
    asset_action: typeof providerOutput.asset_action === "string" && providerOutput.asset_action.trim()
      ? providerOutput.asset_action.trim()
      : assetActionForType(prescription.type),
    currentAssetContext: context.currentAssetContext || providerOutput.currentAssetContext,
    assetSummary: context.currentAssetContext || providerOutput.assetSummary,
    assetContext: {
      ...assetContext,
      mode: targetLocation.mode,
      targetAsset: prescription.label || providerOutput.targetAsset || candidateLabel,
      competitor: assetContext.competitor || context.competitorOrEntity || null,
      evidencePattern: assetContext.evidencePattern || context.evidencePattern || null,
      expectedResult: assetContext.expectedResult || context.expectedResult || null,
    },
  };
}

export function toLegacyContentBrief(output, input = {}) {
  if (!output) return null;
  return {
    ...output,
    title: output.outputTitle,
    accountName: text(input.accountName, "This account"),
    recommendationTitle: output.opportunityTitle,
    objective: output.recommendedAngle,
    suggestedAngle: output.recommendedAngle,
    outline: output.sectionOutline,
    recommendedSections: output.sectionOutline,
    schemaOrPageNotes: output.pageOrSchemaNotes,
    internalNotesExcludedFromCustomerView: true,
    generatedAt: null,
    idempotencyKey: input.idempotencyKey || buildCaiOpportunityIdempotencyKey(input),
  };
}

export const CAI_OUTPUT_STATUS = Object.freeze({
  pending: "pending",
  running: "running",
  completed: "completed",
  failed: "failed",
  needsReview: "needs_review",
  quotaExhausted: "quota_exhausted",
  needsConfiguration: "needs_configuration",
});
